<template>
  <div class="report-configuration-page">
    <div class="container-fluid py-1">
      <div class="row">
        <div class="col-12">
          <div class="card my-4">
            <div class="card-header p-0 position-relative mt-n4 mx-3 z-index-2">
              <div
                class="bg-gradient-primary shadow-primary border-radius-lg pt-4 pb-3"
              >
                <h6 class="text-white text-capitalize ps-3">
                  Configure Reports
                </h6>
                <p class="text-white text-sm ps-3 mb-0">
                  Configure reporting requirements for your organization
                </p>
              </div>
            </div>

            <div class="card-body p-4">
              <div class="configuration-card">
                <div class="step-indicator mb-4">
                  <div
                    class="d-flex justify-content-between align-items-center"
                  >
                    <div class="step" :class="{ active: currentStep === 1 }">
                      <div class="step-circle">1</div>
                      <div class="step-label">License Group</div>
                    </div>
                    <div class="step-divider"></div>
                    <div class="step" :class="{ active: currentStep === 2 }">
                      <div class="step-circle">2</div>
                      <div class="step-label">Licenses & Reports</div>
                    </div>
                    <div class="step-divider"></div>
                    <div class="step" :class="{ active: currentStep === 3 }">
                      <div class="step-circle">3</div>
                      <div class="step-label">Game Categories</div>
                    </div>
                    <div class="step-divider"></div>
                    <div class="step" :class="{ active: currentStep === 4 }">
                      <div class="step-circle">4</div>
                      <div class="step-label">Review</div>
                    </div>
                  </div>
                </div>

                <div v-if="currentStep === 1" class="step-content">
                  <h5 class="font-weight-bold text-dark mb-3">
                    Select License Group
                  </h5>
                  <p class="text-muted mb-3">
                    Choose the approved license group(s) available to your operator:
                  </p>

                  <div class="row mb-4">
                    <div class="col-md-6 mb-3">
                      <div
                        class="operation-card"
                        :class="{ selected: selectedOperationTypes.online, disabled: !availableGroups.online.available }"
                        @click="toggleOperationType('online')"
                      >
                        <div class="operation-icon mb-2">
                          <i class="fas fa-desktop fa-xl"></i>
                        </div>
                        <h6 class="font-weight-bold mb-1">Online Betting Licences</h6>
                        <p class="text-muted small mb-2">
                          Approved online licenses for this operator
                        </p>
                        <div>
                          <span class="badge bg-primary">{{ availableGroups.online.count }} license(s)</span>
                        </div>
                      </div>
                    </div>
                    <div class="col-md-6 mb-3">
                      <div
                        class="operation-card"
                        :class="{ selected: selectedOperationTypes.landBased, disabled: !availableGroups.landBased.available }"
                        @click="toggleOperationType('landBased')"
                      >
                        <div class="operation-icon mb-2">
                          <i class="fas fa-building fa-xl"></i>
                        </div>
                        <h6 class="font-weight-bold mb-1">
                          Landbased Licences
                        </h6>
                        <p class="text-muted small mb-2">
                          Approved land licenses for this operator
                        </p>
                        <div>
                          <span class="badge bg-warning"
                            >{{ availableGroups.landBased.count }} license(s)</span
                          >
                        </div>
                      </div>
                    </div>
                  </div>

                  <div class="d-flex justify-content-end">
                    <button
                      class="btn btn-success btn-sm"
                      @click="goToStep2"
                      :disabled="!hasSelectedOperationTypes"
                    >
                      Continue to Licenses
                      <i class="fas fa-arrow-right ms-1"></i>
                    </button>
                  </div>
                </div>

                <div v-if="currentStep === 2" class="step-content">
                  <h5 class="font-weight-bold text-dark mb-3">
                    Select Licenses and Reports
                  </h5>
                  <p class="text-muted mb-3">
                    First select license(s), then select reports supported by those licenses:
                  </p>

                  <div class="alert alert-info mb-3 py-2">
                    <i class="fas fa-info-circle me-2"></i>
                    Reports and categories are filtered based on selected licenses.
                    <div class="small mt-2">
                      <strong>How to read report labels:</strong>
                      <span class="ms-1"><strong>Required</strong> means you must configure this report for the selected license.</span>
                      <span class="ms-1"><strong>Optional</strong> means you may configure it if it applies to your operation.</span>
                      <span class="ms-1"><strong>Optional (First time)</strong> means it is usually needed during initial setup, then only when there are setup changes.</span>
                    </div>
                    <div class="small mt-1">
                      <strong>Configuration note:</strong>
                      Reports marked <strong>Needs configuration</strong> are the ones you should edit.
                      Reports marked <strong>Auto-saved</strong> do not need manual configuration here.
                    </div>
                  </div>

                  <div class="section-card mb-3">
                    <div class="section-header py-2">
                      <h6 class="mb-0">Select Licenses</h6>
                    </div>
                    <div class="section-body py-3">
                      <div v-if="licensesForSelectedGroups.length === 0" class="text-muted small">
                        No approved licenses found in selected group(s).
                      </div>
                      <div v-else class="row">
                        <div
                          v-for="license in licensesForSelectedGroups"
                          :key="license.license_record_id"
                          class="col-md-6 mb-2"
                        >
                          <div
                            class="category-item license-card"
                            :class="{
                              selected: isLicenseSelected(license.license_record_id),
                              'license-tab-active':
                                isLicenseSelected(license.license_record_id) &&
                                Number(activeLicenseRecordId) === Number(license.license_record_id),
                            }"
                            @click="handleLicenseCardClick(license.license_record_id)"
                          >
                            <div class="d-flex align-items-center">
                              <div class="flex-grow-1">
                                <h6 class="mb-0 font-weight-bold small">{{ license.license_name }}</h6>
                                <small class="text-muted">{{ license.group_label }} - {{ license.license_id }}</small>
                              </div>
                              <div class="text-end">
                                <small
                                  v-if="
                                    isLicenseSelected(license.license_record_id) &&
                                    Number(activeLicenseRecordId) === Number(license.license_record_id)
                                  "
                                  class="text-primary fw-bold d-block mb-1"
                                >
                                  Active
                                </small>
                                <i
                                  v-if="isLicenseSelected(license.license_record_id)"
                                  class="fas fa-check-circle text-success"
                                ></i>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div class="row">
                    <div
                      v-for="report in reportsForActiveLicense"
                      :key="report.id"
                      class="col-md-4 mb-3"
                    >
                      <div
                        class="report-card"
                        :class="{
                          selected: selectedReports.includes(report.id),
                        }"
                        @click="toggleReport(report.id)"
                      >
                        <span
                          class="requirement-chip"
                          :class="getRequirementChipClassForLicense(report.id, activeLicenseRecordId)"
                        >
                          {{ getRequirementLabelForLicense(report.id, activeLicenseRecordId) }}
                        </span>
                        <div
                          class="d-flex justify-content-between align-items-start mb-2"
                        >
                          <h6 class="font-weight-bold mb-0">
                            {{ report.name }}
                          </h6>
                          <span
                            class="badge"
                            :class="
                              getOperationTypeBadgeClass(report.operationType)
                            "
                          >
                            {{
                              report.operationType === "Online"
                                ? "Online"
                                : "Land"
                            }}
                          </span>
                        </div>
                        <p class="text-muted small mb-0">
                          {{ report.description }}
                        </p>
                        <div class="mt-2 d-flex gap-2 flex-wrap">
                          <span
                            class="status-pill"
                            :class="
                              isReportConfigurableForLicense(report.id, activeLicenseRecordId)
                                ? 'status-pill-config'
                                : 'status-pill-auto'
                            "
                          >
                            {{
                              isReportConfigurableForLicense(report.id, activeLicenseRecordId)
                                ? "Needs configuration"
                                : "Auto-saved"
                            }}
                          </span>
                          <div
                            v-if="isReportAlreadyConfiguredForLicense(report.id, activeLicenseRecordId)"
                            class="status-pill status-pill-saved"
                          >
                            <i class="fas fa-check-circle me-1"></i>Saved
                          </div>
                        </div>
                        <div v-if="getGameCategoriesForReportForLicense(report.id, activeLicenseRecordId).length === 1" class="mt-2">
                          <small class="text-muted">
                            <strong>{{
                              getSingleCategoryNameForLicense(report.id, activeLicenseRecordId)
                            }}</strong>
                          </small>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div class="d-flex justify-content-between mt-3">
                    <button
                      class="btn btn-outline-secondary btn-sm"
                      @click="currentStep = 1"
                    >
                      <i class="fas fa-arrow-left me-1"></i>
                      Back
                    </button>
                    <button
                      class="btn btn-success btn-sm"
                      @click="goToStep3"
                      :disabled="selectedLicenseRecordIds.length === 0 || selectedReports.length === 0"
                    >
                      Continue to Categories
                      <i class="fas fa-arrow-right ms-1"></i>
                    </button>
                  </div>
                </div>

                <div v-if="currentStep === 3" class="step-content">
                  <h5 class="font-weight-bold text-dark mb-3">
                    Select Game Categories
                  </h5>
                  <p class="text-muted mb-3">
                    Choose game categories for each selected report:
                  </p>
                  <div class="alert alert-secondary py-2 mb-3">
                    Only reports marked <strong>Needs configuration</strong> require category selection here.
                    Auto-saved reports are handled by the system.
                  </div>

                  <div
                    v-if="selectedReports.length === 0"
                    class="alert alert-warning mb-3 py-2"
                  >
                    <i class="fas fa-exclamation-triangle me-2"></i>
                    Please select at least one report first.
                  </div>

                  <div
                    v-for="report in selectedConfigurableReports"
                    :key="report.id"
                    class="mb-3"
                  >
                    <div class="report-section-card mb-2">
                      <div class="report-section-header py-2">
                        <div
                          class="d-flex justify-content-between align-items-center"
                        >
                          <h6 class="mb-0 font-weight-bold">
                            {{ report.name }}
                          </h6>
                          <span
                            v-if="getGameCategoriesForReport(report.id).length === 1"
                            class="badge bg-success"
                          >
                          </span>
                        </div>
                      </div>
                      <div class="report-section-body py-3">
                        <div
                          v-if="getGameCategoriesForReport(report.id).length === 1"
                          class="text-success small"
                        >
                          <strong>{{
                            getSingleCategoryName(report.id)
                          }}</strong>
                        </div>
                        <div
                          v-else-if="
                            getGameCategoriesForReport(report.id).length === 0
                          "
                          class="text-muted small"
                        >
                          No game categories available for this report
                        </div>
                        <div v-else class="row">
                          <div
                            v-for="category in getGameCategoriesForReport(
                              report.id,
                            )"
                            :key="category.id"
                            class="col-md-6 mb-2"
                          >
                            <div
                              class="category-item"
                              :class="{
                                selected: isCategorySelected(
                                  report.id,
                                  category.id,
                                ),
                              }"
                              @click="toggleCategory(report.id, category.id)"
                            >
                              <div class="d-flex align-items-center">
                                <div class="flex-grow-1">
                                  <h6 class="mb-0 font-weight-bold small">
                                    {{ category.name }}
                                  </h6>
                                </div>
                                <div class="text-end">
                                  <i
                                    v-if="
                                      isCategorySelected(report.id, category.id)
                                    "
                                    class="fas fa-check-circle text-success"
                                  ></i>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div
                    v-if="selectedConfigurableReports.length === 0 && selectedReports.length > 0"
                    class="alert alert-info py-2"
                  >
                    Selected reports are auto-saved for your licenses. No manual category configuration is required.
                  </div>

                  <div class="d-flex justify-content-between mt-3">
                    <button
                      class="btn btn-outline-secondary btn-sm"
                      @click="currentStep = 2"
                    >
                      <i class="fas fa-arrow-left me-1"></i>
                      Back
                    </button>
                    <button class="btn btn-success btn-sm" @click="goToStep4">
                      Review Configuration
                      <i class="fas fa-eye ms-1"></i>
                    </button>
                  </div>
                </div>

                <div v-if="currentStep === 4" class="step-content">
                  <h5 class="font-weight-bold text-dark mb-3">
                    Review Configuration
                  </h5>

                  <div class="section-card mb-3">
                    <div class="section-header py-2">
                      <h6 class="mb-0">Selected Licenses</h6>
                    </div>
                    <div class="section-body py-3">
                      <div class="d-flex flex-wrap gap-2" v-if="selectedLicenses.length">
                        <span
                          v-for="license in selectedLicenses"
                          :key="license.license_record_id"
                          class="badge bg-info text-dark small"
                        >
                          {{ license.license_name }} - {{ license.license_id }}
                        </span>
                      </div>
                      <div v-else class="text-muted small">No licenses selected.</div>
                    </div>
                  </div>

                  <!-- <div class="section-card mb-3">
                    <div class="section-header py-2">
                      <h6 class="mb-0">Data Format Preference</h6>
                    </div>
                    <div class="section-body py-3">
                      <div class="mb-2">
                        <label class="form-label fw-bold small">
                          Select default data format for report previews:
                        </label>
                        <div class="d-flex gap-3">
                          <div class="form-check">
                            <input
                              class="form-check-input"
                              type="radio"
                              name="dataFormat"
                              id="formatJson"
                              value="1"
                              v-model="selectedDataFormat"
                            />
                            <label
                              class="form-check-label small"
                              for="formatJson"
                            >
                              JSON
                            </label>
                          </div>
                          <div class="form-check">
                            <input
                              class="form-check-input"
                              type="radio"
                              name="dataFormat"
                              id="formatXml"
                              value="2"
                              v-model="selectedDataFormat"
                            />
                            <label
                              class="form-check-label small"
                              for="formatXml"
                            >
                              XML
                            </label>
                          </div>
                        </div>
                      </div>
                      <small class="text-muted">
                        This format will be used when previewing your reports
                        after submission.
                      </small>
                    </div>
                  </div> -->

                  <div class="section-card mb-3">
                    <div class="section-header py-2">
                      <h6 class="mb-0">Selected Reports</h6>
                    </div>
                    <div class="section-body py-3">
                      <p class="small text-muted mb-2">
                        Click a report below to switch the preview.
                      </p>
                      <div class="d-flex flex-wrap gap-2">
                        <span
                          v-for="reportId in selectedReports"
                          :key="reportId"
                          class="badge clickable-badge small"
                          :class="[getReportBadgeClass(reportId), { 'clickable-badge-active': isPreviewReportActive(reportId) }]"
                          @click="selectReportForPreview(reportId)"
                          :title="`Click to view ${getReportName(
                            reportId,
                          )} preview`"
                        >
                          {{ getReportName(reportId) }}
                        </span>
                      </div>
                    </div>
                  </div>

                  <div class="section-card mb-3">
                    <div
                      class="section-header py-2 d-flex justify-content-between align-items-center"
                    >
                      <h6 class="mb-0">
                        Preview:
                        {{
                          selectedPreviewReport
                            ? getReportName(selectedPreviewReport)
                            : selectedReports.length > 0
                            ? getReportName(selectedReports[0])
                            : "No Reports"
                        }}
                        <span
                          v-if="
                            selectedPreviewReport &&
                            getGameCategoriesForReport(selectedPreviewReport).length === 1
                          "
                          class="badge bg-success ms-2"
                        >
                        </span>
                      </h6>
                      <div class="d-flex align-items-center">
                        <div class="preview-tabs me-2">
                          <button
                            class="preview-tab"
                            :class="{ active: previewFormat === 'json' }"
                            @click="previewFormat = 'json'"
                          >
                            JSON
                          </button>
                          <button
                            class="preview-tab"
                            :class="{ active: previewFormat === 'xml' }"
                            @click="previewFormat = 'xml'"
                          >
                            XML
                          </button>
                        </div>
                        <button
                          class="btn btn-outline-secondary btn-sm me-2"
                          @click="copyToClipboard"
                          title="Copy to clipboard"
                        >
                          <i class="fas fa-copy"></i>
                        </button>
                        <button
                          class="btn btn-outline-secondary btn-sm"
                          @click="toggleFullscreen"
                          title="Toggle fullscreen"
                        >
                          <i class="fas fa-expand"></i>
                        </button>
                      </div>
                    </div>
                    <div class="section-body py-3">
                      <!-- Fullscreen overlay with close button -->
                      <div v-if="isFullscreen" class="fullscreen-overlay">
                        <div class="fullscreen-header">
                          <div>
                            <strong>
                              {{
                                selectedPreviewReport
                                  ? getReportName(selectedPreviewReport)
                                  : selectedReports.length > 0
                                  ? getReportName(selectedReports[0])
                                  : "No Reports"
                              }}
                            </strong>
                            <span class="text-muted ms-2"
                              >(Fullscreen Mode)</span
                            >
                          </div>
                          <div class="fullscreen-controls">
                            <div class="preview-tabs me-2">
                              <button
                                class="preview-tab"
                                :class="{ active: previewFormat === 'json' }"
                                @click="previewFormat = 'json'"
                              >
                                JSON
                              </button>
                              <button
                                class="preview-tab"
                                :class="{ active: previewFormat === 'xml' }"
                                @click="previewFormat = 'xml'"
                              >
                                XML
                              </button>
                            </div>
                            <button
                              class="btn btn-outline-secondary btn-sm me-2"
                              @click="copyToClipboard"
                              title="Copy to clipboard"
                            >
                              <i class="fas fa-copy"></i>
                            </button>
                            <button
                              class="btn btn-danger btn-sm"
                              @click="toggleFullscreen"
                              title="Exit fullscreen"
                            >
                              <i class="fas fa-times"></i> Exit
                            </button>
                          </div>
                        </div>
                        <div class="fullscreen-content">
                          <pre
                            class="code-display-box fullscreen"
                          ><code class="hljs" v-html="highlightedPreview"></code></pre>
                        </div>
                      </div>

                      <!-- Regular preview (non-fullscreen) -->
                      <div v-else>
                        <pre
                          class="code-display-box"
                        ><code class="hljs" v-html="highlightedPreview"></code></pre>
                      </div>
                    </div>
                  </div>

                  <div class="d-flex justify-content-between">
                    <button
                      class="btn btn-outline-secondary btn-sm"
                      @click="currentStep = 3"
                    >
                      <i class="fas fa-edit me-1"></i>
                      Edit
                    </button>
                    <div>
                      <button
                        class="btn btn-outline-warning btn-sm me-2"
                        @click="resetSelections"
                      >
                        <i class="fas fa-redo me-1"></i>
                        Reset
                      </button>
                      <button
                        class="btn btn-success btn-sm"
                        @click="handleFinalSubmit"
                        :disabled="isLoading"
                      >
                        <span v-if="isLoading">
                          <i class="fas fa-spinner fa-spin me-1"></i>
                          Saving...
                        </span>
                        <span v-else>
                          <i class="fas fa-check-circle me-1"></i>
                          Save Configuration
                        </span>
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import axios from "axios";
import { resolveFieldHelp } from "@/data/ncemsFieldHelp";

export default {
  name: "ReportConfiguration",
  data() {
    return {
      currentStep: 1,
      isFullscreen: false,
      selectedDataFormat: "1",
      selectedOperationTypes: {
        online: false,
        landBased: false,
      },
      selectedLicenseRecordIds: [],
      selectedReports: [],
      selectedCategories: [],
      previewFormat: "json",
      selectedPreviewReport: null,
      isLoading: false,
      reportConfigContext: { groups: [] },
      savedSelectionsByLicenseReport: {},
      activeLicenseRecordId: null,
    };
  },
  mounted() {
    this.loadReportConfigurationContext();
  },
  computed: {
    availableGroups() {
      const groups = this.reportConfigContext?.groups || [];
      const online = groups.find((g) => g.group === "online");
      const land = groups.find((g) => g.group === "land");
      return {
        online: {
          available: !!online,
          count: (online?.licenses || []).length,
          label: online?.group_label || "Online Betting Licences",
        },
        landBased: {
          available: !!land,
          count: (land?.licenses || []).length,
          label: land?.group_label || "Landbased Licences",
        },
      };
    },
    selectedGroupKeys() {
      const keys = [];
      if (this.selectedOperationTypes.online) keys.push("online");
      if (this.selectedOperationTypes.landBased) keys.push("land");
      return keys;
    },
    licensesForSelectedGroups() {
      const groups = this.reportConfigContext?.groups || [];
      const picked = [];
      groups.forEach((groupItem) => {
        if (!this.selectedGroupKeys.includes(groupItem.group)) return;
        (groupItem.licenses || []).forEach((licenseItem) => picked.push(licenseItem));
      });
      return picked;
    },
    selectedLicenses() {
      const selectedSet = new Set(this.selectedLicenseRecordIds.map((id) => Number(id)));
      return this.licensesForSelectedGroups.filter((item) =>
        selectedSet.has(Number(item.license_record_id)),
      );
    },
    hasSelectedOperationTypes() {
      return this.selectedGroupKeys.length > 0;
    },
    hasSelectedConfiguration() {
      return (
        this.selectedLicenseRecordIds.length > 0 ||
        this.selectedReports.length > 0 ||
        this.hasSelectedOperationTypes
      );
    },
    supportedReportIds() {
      const supported = new Set();
      this.selectedLicenses.forEach((licenseItem) => {
        (licenseItem.reports || []).forEach((report) => supported.add(report.report_id));
      });
      return supported;
    },
    filteredReports() {
      if (!this.selectedLicenses.length) return [];
      const reports = this.getReports();
      return reports
        .filter((report) => this.supportedReportIds.has(report.id))
        .sort((a, b) => {
          const aConfigurable = this.isReportConfigurable(a.id) ? 1 : 0;
          const bConfigurable = this.isReportConfigurable(b.id) ? 1 : 0;
          if (aConfigurable !== bConfigurable) return bConfigurable - aConfigurable;
          return a.name.localeCompare(b.name);
        });
    },
    activeLicense() {
      if (!this.selectedLicenses.length) return null;
      const active = this.selectedLicenses.find(
        (license) => Number(license.license_record_id) === Number(this.activeLicenseRecordId),
      );
      return active || this.selectedLicenses[0];
    },
    reportsForActiveLicense() {
      if (!this.activeLicense) return [];
      const supportedIds = new Set(
        (this.activeLicense.reports || []).map((report) => report.report_id),
      );
      const reports = this.getReports().filter((report) => supportedIds.has(report.id));
      return reports.sort((a, b) => {
        const aConfigurable = this.isReportConfigurableForLicense(
          a.id,
          this.activeLicense.license_record_id,
        )
          ? 1
          : 0;
        const bConfigurable = this.isReportConfigurableForLicense(
          b.id,
          this.activeLicense.license_record_id,
        )
          ? 1
          : 0;
        if (aConfigurable !== bConfigurable) return bConfigurable - aConfigurable;
        return a.name.localeCompare(b.name);
      });
    },
    selectedReportsDetails() {
      const reports = this.getReports();
      return reports.filter((report) => this.selectedReports.includes(report.id));
    },
    selectedConfigurableReports() {
      return this.selectedReportsDetails.filter((report) =>
        this.isReportConfigurable(report.id),
      );
    },
    hasAutoSelectedCategories() {
      return this.selectedReports.some((reportId) =>
        this.getGameCategoriesForReport(reportId).length === 1,
      );
    },
    autoSelectedReports() {
      return this.selectedReports
        .filter((reportId) => this.getGameCategoriesForReport(reportId).length === 1)
        .map((reportId) => this.getReportName(reportId));
    },
    jsonPreview() {
      const reportId =
        this.selectedPreviewReport ||
        (this.selectedReports.length > 0 ? this.selectedReports[0] : null);

      if (!reportId) return "No reports selected";

      return this.getReportJsonStructure(reportId);
    },
    xmlPreview() {
      const reportId =
        this.selectedPreviewReport ||
        (this.selectedReports.length > 0 ? this.selectedReports[0] : null);

      if (!reportId)
        return '<?xml version="1.0" encoding="UTF-8"?>\n<data>\n  No reports selected\n</data>';

      return this.getReportXmlStructure(reportId);
    },
    highlightedPreview() {
      const code =
        this.previewFormat === "json" ? this.jsonPreview : this.xmlPreview;
      const language = this.previewFormat === "json" ? "json" : "xml";

      if (typeof window !== "undefined" && window.hljs) {
        try {
          const result = window.hljs.highlight(code, { language: language });
          const reportId =
            this.selectedPreviewReport ||
            (this.selectedReports.length > 0 ? this.selectedReports[0] : "");
          return this.annotateFieldHelp(result.value, this.previewFormat, reportId);
        } catch (e) {
          console.warn("Highlight.js highlighting failed:", e);
        }
      }

      return this.escapeHtml(code);
    },
  },
  methods: {
    annotateFieldHelp(highlightedHtml = "", format = "json", reportId = "") {
      if (!highlightedHtml) return highlightedHtml;

      if (format === "json") {
        const keyTokenRegex =
          /<span class="hljs-(?:attr|property|string)">("?)([^"<]+)\1<\/span>/g;

        return highlightedHtml.replace(
          keyTokenRegex,
          (fullMatch, _quote, fieldName, offset, sourceHtml) => {
            const tail = sourceHtml.slice(offset + fullMatch.length, offset + fullMatch.length + 180);
            const isKey = /^(?:\s|<[^>]+>)*(?::|<span class="hljs-punctuation">:<\/span>)/.test(
              tail,
            );

            if (!isKey) return fullMatch;

            const help = resolveFieldHelp({
              reportId,
              key: fieldName,
              path: fieldName,
            });
            return `<span class="field-help-token" data-help="${this.escapeHtmlAttribute(help)}">${fullMatch}</span>`;
          },
        );
      }

      return highlightedHtml.replace(
        /<span class="hljs-name">([a-zA-Z_][\w:-]*)<\/span>/g,
        (fullMatch, fieldName) => {
          const help = resolveFieldHelp({
            reportId,
            key: fieldName,
            path: fieldName,
          });
          return `<span class="field-help-token" data-help="${this.escapeHtmlAttribute(help)}">${fullMatch}</span>`;
        },
      );
    },
    escapeHtmlAttribute(value = "") {
      return String(value)
        .replace(/&/g, "&amp;")
        .replace(/"/g, "&quot;")
        .replace(/</g, "&lt;")
        .replace(/>/g, "&gt;");
    },
    escapeHtml(text) {
      const div = document.createElement("div");
      div.textContent = text;
      return div.innerHTML;
    },
    async loadReportConfigurationContext() {
      try {
        const [contextResponse, selectionsResponse] = await Promise.all([
          axios.get("/report-config/context/"),
          axios.get("/report-config/selections/"),
        ]);
        this.reportConfigContext = contextResponse.data || { groups: [] };
        this.savedSelectionsByLicenseReport = this.buildSavedSelectionMap(
          selectionsResponse.data || {},
        );
      } catch (error) {
        console.warn("Failed to load report config context", error);
        this.reportConfigContext = { groups: [] };
        this.savedSelectionsByLicenseReport = {};
      }
    },
    buildSavedSelectionMap(payload) {
      const map = {};
      (payload.licenses || []).forEach((licenseItem) => {
        (licenseItem.reports || []).forEach((report) => {
          map[`${licenseItem.license_record_id}::${report.report_id}`] = {
            game_unique_names: report.game_unique_names || [],
            category_count: Number(report.category_count || 0),
            updated_at: report.updated_at || null,
          };
        });
      });
      return map;
    },

    isLicenseSelected(licenseRecordId) {
      return this.selectedLicenseRecordIds.includes(Number(licenseRecordId));
    },

    toggleLicenseSelection(licenseRecordId) {
      const normalizedId = Number(licenseRecordId);
      const index = this.selectedLicenseRecordIds.indexOf(normalizedId);
      if (index > -1) {
        this.selectedLicenseRecordIds.splice(index, 1);
        if (Number(this.activeLicenseRecordId) === normalizedId) {
          this.activeLicenseRecordId =
            this.selectedLicenseRecordIds.length > 0
              ? this.selectedLicenseRecordIds[0]
              : null;
        }
      } else {
        this.selectedLicenseRecordIds.push(normalizedId);
        if (!this.activeLicenseRecordId) {
          this.activeLicenseRecordId = normalizedId;
        }
      }

      const allowedReportIds = new Set(this.filteredReports.map((r) => r.id));
      this.selectedReports = this.selectedReports.filter((reportId) => allowedReportIds.has(reportId));
      this.updateCategoriesAfterReportChange();
    },
    handleLicenseCardClick(licenseRecordId) {
      const normalizedId = Number(licenseRecordId);
      if (this.isLicenseSelected(normalizedId)) {
        this.setActiveLicenseTab(normalizedId);
        return;
      }
      this.toggleLicenseSelection(normalizedId);
      this.setActiveLicenseTab(normalizedId);
    },
    setActiveLicenseTab(licenseRecordId) {
      this.activeLicenseRecordId = Number(licenseRecordId);
    },

    getReportMetaForSelectedLicenses(reportId) {
      const metas = [];
      this.selectedLicenses.forEach((licenseItem) => {
        const meta = (licenseItem.reports || []).find((r) => r.report_id === reportId);
        if (meta) metas.push(meta);
      });
      return metas;
    },
    getReportMetaForLicense(reportId, licenseRecordId) {
      const license = this.selectedLicenses.find(
        (item) => Number(item.license_record_id) === Number(licenseRecordId),
      );
      if (!license) return null;
      return (license.reports || []).find((r) => r.report_id === reportId) || null;
    },
    isReportConfigurable(reportId) {
      const metas = this.getReportMetaForSelectedLicenses(reportId);
      return metas.some((meta) => !!meta.is_configurable);
    },
    isReportConfigurableForLicense(reportId, licenseRecordId) {
      const meta = this.getReportMetaForLicense(reportId, licenseRecordId);
      return !!meta?.is_configurable;
    },
    isReportAlreadyConfigured(reportId) {
      if (!this.selectedLicenses.length) return false;
      return this.selectedLicenses.some((licenseItem) => {
        const key = `${licenseItem.license_record_id}::${reportId}`;
        return !!this.savedSelectionsByLicenseReport[key];
      });
    },
    isReportAlreadyConfiguredForLicense(reportId, licenseRecordId) {
      const key = `${licenseRecordId}::${reportId}`;
      return !!this.savedSelectionsByLicenseReport[key];
    },
    getRequirementFallback(reportId, licenseItem = null) {
      const normalizedLicenseName = String(licenseItem?.license_name || "")
        .trim()
        .toLowerCase();

      if (
        reportId === "jackpot_balance" &&
        normalizedLicenseName.includes("general betting license")
      ) {
        return "Optional";
      }

      return "Required";
    },
    getResolvedRequirementLabel(reportId, licenseItem = null, rawLabel = "") {
      const forcedLabel = this.getRequirementFallback(reportId, licenseItem);
      const normalizedLicenseName = String(licenseItem?.license_name || "")
        .trim()
        .toLowerCase();

      if (
        reportId === "jackpot_balance" &&
        normalizedLicenseName.includes("general betting license")
      ) {
        return forcedLabel;
      }

      return rawLabel || forcedLabel;
    },
    getRequirementLabel(reportId) {
      const matchingLicenses = this.selectedLicenses.filter((licenseItem) =>
        (licenseItem.reports || []).some((report) => report.report_id === reportId),
      );
      if (!matchingLicenses.length) return "Required";
      const labels = [
        ...new Set(
          matchingLicenses.map((licenseItem) => {
            const meta = (licenseItem.reports || []).find(
              (report) => report.report_id === reportId,
            );
            return this.getResolvedRequirementLabel(
              reportId,
              licenseItem,
              meta?.requirement_label,
            );
          }),
        ),
      ];
      if (labels.length === 1) return labels[0];
      return "Mixed by license";
    },
    getRequirementLabelForLicense(reportId, licenseRecordId) {
      const licenseItem = this.selectedLicenses.find(
        (item) => Number(item.license_record_id) === Number(licenseRecordId),
      );
      const meta = this.getReportMetaForLicense(reportId, licenseRecordId);
      return this.getResolvedRequirementLabel(
        reportId,
        licenseItem,
        meta?.requirement_label,
      );
    },
    getRequirementBadgeClass(reportId) {
      const label = this.getRequirementLabel(reportId);
      if (label === "Required") return "bg-danger";
      if (label === "Optional") return "bg-secondary";
      if (label === "Optional (First time)") return "bg-warning text-dark";
      return "bg-info";
    },
    getRequirementChipClass(reportId) {
      const label = this.getRequirementLabel(reportId);
      if (label === "Required") return "chip-required";
      if (label === "Optional") return "chip-optional";
      if (label === "Optional (First time)") return "chip-first-time";
      return "chip-default";
    },
    getRequirementChipClassForLicense(reportId, licenseRecordId) {
      const label = this.getRequirementLabelForLicense(reportId, licenseRecordId);
      if (label === "Required") return "chip-required";
      if (label === "Optional") return "chip-optional";
      if (label === "Optional (First time)") return "chip-first-time";
      return "chip-default";
    },
    goToStep2() {
      if (!this.hasSelectedOperationTypes) {
        this.showNotification(
          "Please select at least one license group",
          "error",
        );
        return;
      }
      if (!this.licensesForSelectedGroups.length) {
        this.showNotification(
          "No approved licenses were found in the selected group(s).",
          "error",
        );
        return;
      }
      this.selectedLicenseRecordIds = [];
      this.selectedReports = [];
      this.selectedCategories = [];
      this.activeLicenseRecordId = null;
      this.currentStep = 2;
    },

    goToStep3() {
      if (this.selectedLicenseRecordIds.length === 0) {
        this.showNotification("Please select at least one license", "error");
        return;
      }
      if (this.selectedReports.length === 0) {
        this.showNotification("Please select at least one report", "error");
        return;
      }

      this.autoSelectCategories();
      this.currentStep = 3;
    },

    goToStep4() {
      this.selectedPreviewReport =
        this.selectedReports.length > 0 ? this.selectedReports[0] : null;
      this.currentStep = 4;
    },

    getReports() {
      return [
        {
          id: "player_registration",
          name: "Player Registration",
          operationType: "Online",
          description: "Player registration information",
          hasSingleCategory: false,
        },
        {
          id: "player_activity",
          name: "Player Activity",
          operationType: "Online",
          description: "Player activity tracking",
          hasSingleCategory: false,
        },
        {
          id: "jackpot_balance",
          name: "Jackpot Balance",
          operationType: "Online",
          description: "Jackpot balance information",
          hasSingleCategory: false,
        },
        {
          id: "game_summary",
          name: "Game Summary",
          operationType: "Online",
          description: "Game summary data",
          hasSingleCategory: false,
        },
        {
          id: "player_summary",
          name: "Player Summary",
          operationType: "Online",
          description: "Player summary information",
          hasSingleCategory: false,
        },
        {
          id: "game_funds_in_play",
          name: "Game Funds In Play",
          operationType: "Online",
          description: "Funds in play tracking",
          hasSingleCategory: false,
        },
        {
          id: "land_summary",
          name: "Land Summary",
          operationType: "Land-based",
          description: "Land-based operation summary",
          hasSingleCategory: false,
        },
        {
          id: "land_event",
          name: "Land Event",
          operationType: "Land-based",
          description: "Land-based events tracking",
          hasSingleCategory: true,
        },
        {
          id: "terminal_config",
          name: "Terminal Config",
          operationType: "Land-based",
          description: "Terminal configuration data",
          hasSingleCategory: false,
        },
      ];
    },

    getReportById(reportId) {
      return this.getReports().find((r) => r.id === reportId);
    },

    getReportBadgeClass(reportId) {
      if (this.getGameCategoriesForReport(reportId).length === 1) {
        return "bg-success";
      }
      return "bg-primary";
    },

    getAllCategories() {
      return [
        { id: "RRI_casinoGame", name: "Casino Games", value: "RRI_casinoGame" },
        {
          id: "RRI_casinoTourn",
          name: "Casino Tournaments",
          value: "RRI_casinoTourn",
        },
        {
          id: "RRI_fixedOdds",
          name: "Fixed Odds Betting",
          value: "RRI_fixedOdds",
        },
        {
          id: "RRI_betExchange",
          name: "Betting Exchanges",
          value: "RRI_betExchange",
        },
        {
          id: "RRI_pariMutuel",
          name: "Pari-mutuel Betting",
          value: "RRI_pariMutuel",
        },
        {
          id: "RRI_pokerCash",
          name: "Poker Cash Games",
          value: "RRI_pokerCash",
        },
        {
          id: "RRI_pokerTourn",
          name: "Poker Tournaments",
          value: "RRI_pokerTourn",
        },
        { id: "RRI_fantasy", name: "Fantasy Games", value: "RRI_fantasy" },
        { id: "RRI_bingo", name: "Bingo Games", value: "RRI_bingo" },
        {
          id: "RRI_landSlot",
          name: "Land-based Slot Games",
          value: "RRI_landSlot",
        },
        {
          id: "RRI_landTable",
          name: "Land-based Table Games",
          value: "RRI_landTable",
        },
        {
          id: "RRI_landFixedOdds",
          name: "Land-based Fixed Odds",
          value: "RRI_landFixedOdds",
        },
      ];
    },

    getReportGameCategoryMapping() {
      return {
        player_registration: [],
        player_activity: [],
        jackpot_balance: [],
        game_summary: [
          "RRI_casinoGame",
          "RRI_casinoTourn",
          "RRI_fixedOdds",
          "RRI_betExchange",
          "RRI_pariMutuel",
          "RRI_pokerCash",
          "RRI_pokerTourn",
          "RRI_fantasy",
          "RRI_bingo",
          //"RRI_landFixedOdds",
        ],
        player_summary: [],
        game_funds_in_play: [
          "RRI_casinoGame",
          "RRI_casinoTourn",
          "RRI_fixedOdds",
          "RRI_betExchange",
          "RRI_pariMutuel",
          "RRI_pokerCash",
          "RRI_pokerTourn",
          "RRI_fantasy",
          "RRI_bingo",
          // "RRI_landFixedOdds",
        ],
        land_summary: ["RRI_landSlot", "RRI_landTable"],
        land_event: ["RRI_landSlot"],
        terminal_config: ["RRI_landSlot", "RRI_landTable"],
      };
    },

    getSingleCategoryName(reportId) {
      const mapping = this.getGameCategoriesForReport(reportId).map((cat) => cat.id);
      if (mapping && mapping.length === 1) {
        const category = this.getAllCategories().find(
          (cat) => cat.id === mapping[0],
        );
        return category ? category.name : mapping[0];
      }
      return "";
    },

    autoSelectCategories() {
      this.selectedReports.forEach((reportId) => {
        const mapping = this.getGameCategoriesForReport(reportId).map((cat) => cat.id);
        if (mapping && mapping.length === 1) {
          const categoryId = mapping[0];
          if (!this.isCategorySelected(reportId, categoryId)) {
            this.selectedCategories.push({ reportId, categoryId });
          }
        }
      });
    },

    getReportJsonStructure(reportId) {
      const getDefaultStructure = () => {
        const categories = this.getSelectedCategoriesForReport(reportId);
        const structure = {
          reportId: reportId,
          gameCategories: categories.map((cat) => {
            const category = this.getAllCategories().find(
              (c) => c.id === cat.categoryId,
            );
            return {
              gameCategory: category ? category.value : cat.categoryId,
              gameCategoryName: category ? category.name : cat.categoryId,
            };
          }),
        };
        return JSON.stringify([structure], null, 2);
      };

      const jsonStructureGenerators = {
        terminal_config: () => {
          const selectedCategories =
            this.getSelectedCategoriesForReport("terminal_config");
          const hasLandSlot = selectedCategories.some(
            (cat) => cat.categoryId === "RRI_landSlot",
          );
          const hasLandTable = selectedCategories.some(
            (cat) => cat.categoryId === "RRI_landTable",
          );

          const baseStructure = {
            terminalConfig: [
              {
                brandId: "string | Got from operator NCEMS portal",
                terminal: {
                  terminalId: "string",
                },
                model: {
                  modelId: "string | has_specifics",
                },
                terminalConfigDetail: {
                  terminalName: "string",
                  regulatorId: "string",
                  serialNum: "string",
                  isDeleted: "boolean | has_specifics",
                  fromDateTime: "datetime",
                  toDateTime: "datetime",
                },
              },
            ],
          };

          // Add slotTerminal for landSlot category
          if (hasLandSlot) {
            baseStructure.terminalConfig[0].terminalConfigDetail.slotTerminal =
              {
                egmId: "string",
                machineNum: "string",
                machineType: "string",
                yearMade: "string",
              };

            baseStructure.terminalConfig[0].terminalConfigDetail.rouletteWheel =
              {
                mfgName: "string",
                yearMade: "string",
                cylinderId: "string",
              };
          }

          // Add installedGameList for landTable category
          if (hasLandTable) {
            baseStructure.terminalConfig[0].installedGameList = [
              {
                game: {
                  gameId: "string",
                  gameName: "string",
                  gameCategory: "string | has_specifics",
                  gameType: "string | has_specifics",
                },
              },
            ];
          }

          let result = JSON.stringify(baseStructure, null, 2).replace(
            '"installedGameList": [',
            '\n // Only for landTable category, include installedGameList with game details:\n  "installedGameList": [',
          );

          result = result.replace(
            '"slotTerminal": {',
            '\n // Only for landSlot or EGMs category, include slotTerminal details:\n  "slotTerminal": {',
          );

          result = result.replace(
            '"rouletteWheel": {',
            '\n // For Roulette tables, add rouletteWheel object in terminalConfigDetail\n  "rouletteWheel": {',
          );

          return result;
        },

        land_summary: () => {
          const selectedCategories =
            this.getSelectedCategoriesForReport("land_summary");
          const hasLandSlot = selectedCategories.some(
            (cat) => cat.categoryId === "RRI_landSlot",
          );
          const hasLandTable = selectedCategories.some(
            (cat) => cat.categoryId === "RRI_landTable",
          );

          const baseStructure = {
            landSummary: [
              {
                brandId: "string | Got from operator NCEMS portal",
                terminal: {
                  terminalId: "string",
                },
              },
            ],
          };

          // Add movementList and meterList for landSlot category
          if (hasLandSlot) {
            baseStructure.landSummary[0].movementList = {
              startDateTime: "datetime",
              endDateTime: "datetime",
              movements: [
                {
                  transClass: "string | has_specifics",
                  transType: "string | has_specifics",
                  movementValue: "decimal",
                },
              ],
            };
            baseStructure.landSummary[0].meterList = {
              statusDateTime: "datetime",
              meters: [
                {
                  transClass: "string | has_specifics",
                  transType: "string | has_specifics",
                  meterValue: "decimal",
                },
              ],
            };
          }

          // Add landGameList for landTable category
          if (hasLandTable) {
            baseStructure.landSummary[0].landGameList = {
              landGame: [
                {
                  game: {
                    gameId: "string",
                    gameName: "string",
                    gameType: "string | has_specifics",
                  },
                  gameMovementList: {
                    startDateTime: "datetime",
                    endDateTime: "datetime",
                    movements: [
                      {
                        transClass: "string | has_specifics",
                        transType: "string | has_specifics",
                        movementValue: "decimal",
                      },
                    ],
                  },
                },
              ],
            };
          }

          let result = JSON.stringify(baseStructure, null, 2);

          result = result.replace(
            '"movementList": {',
            '\n // Only for landSlot category, include movementList with transaction details:\n  "movementList": {',
          );

          result = result.replace(
            '"meterList": {',
            '\n // Only for landSlot category, include meterList with meter details:\n  "meterList": {',
          );

          result = result.replace(
            '"landGameList": {',
            '\n // Only for landTable category, include landGameList with game and movement details:\n  "landGameList": {',
          );

          return result;
        },

        game_summary: () => {
          const selectedCategories =
            this.getSelectedCategoriesForReport("game_summary");
          const hasLandFixedOdds = selectedCategories.some(
            (cat) => cat.categoryId === "RRI_landFixedOdds",
          );

          // Build gameSummaryDetail dynamically based on selected categories
          const gameSummaryDetail = {};

          if (
            selectedCategories.some(
              (cat) => cat.categoryId === "RRI_casinoGame",
            )
          ) {
            gameSummaryDetail.casinoGameSummary = {
              playerCnt: "integer",
              freePlayerCnt: "integer",
              stakeAmt: "decimal",
              freeStakeAmt: "decimal",
              refundAmt: "decimal",
              freeRefundAmt: "decimal",
              wagerAmt: "decimal",
              baseWinAmt: "decimal",
              jackpotContribAmt: "decimal",
              jackpotWinAmt: "decimal",
              gamesPlayedCnt: "integer",
              revenueAmt: "decimal",
              networkAdjustAmt: "decimal",
              taxPlayerIncomeAmt: "decimal",
              fundsType: "string | has_specifics",
            };
          }

          if (
            selectedCategories.some(
              (cat) => cat.categoryId === "RRI_casinoTourn",
            )
          ) {
            gameSummaryDetail.casinoTournSummary = {
              playerCnt: "integer",
              freePlayerCnt: "integer",
              stakeAmt: "decimal",
              freeStakeAmt: "decimal",
              refundAmt: "decimal",
              freeRefundAmt: "decimal",
              rakeAmt: "decimal",
              baseWinAmt: "decimal",
              fromPotAmt: "decimal",
              potContribAmt: "decimal",
              potAdditionalAmt: "decimal",
              toPotAmt: "decimal",
              revenueAmt: "decimal",
              networkAdjustAmt: "decimal",
              taxPlayerIncomeAmt: "decimal",
              fundsType: "string | has_specifics",
            };
          }

          if (
            selectedCategories.some((cat) => cat.categoryId === "RRI_fixedOdds")
          ) {
            gameSummaryDetail.fixedOddsSummary = {
              stakeAmt: "decimal",
              freeStakeAmt: "decimal",
              refundAmt: "decimal",
              freeRefundAmt: "decimal",
              forfeitAmt: "decimal",
              baseWinAmt: "decimal",
              adjustmentAmt: "decimal",
              betsPlacedCnt: "integer",
              betsRefundedCnt: "integer",
              betsWonCnt: "integer",
              betsAdjustedCnt: "integer",
              revenueAmt: "decimal",
              networkAdjustAmt: "decimal",
              taxPlayerIncomeAmt: "decimal",
              slipsExpiredCnt: "integer",
              slipsExpiredAmt: "decimal",
              fundsType: "string | has_specifics",
            };
          }

          if (
            selectedCategories.some(
              (cat) => cat.categoryId === "RRI_betExchange",
            )
          ) {
            gameSummaryDetail.exchangeSummary = {
              stakeAmt: "decimal",
              freeStakeAmt: "decimal",
              refundAmt: "decimal",
              freeRefundAmt: "decimal",
              baseWinAmt: "decimal",
              rakeAmt: "decimal",
              adjustmentAmt: "decimal",
              betsPlacedCnt: "integer",
              betsRefundedCnt: "integer",
              betsWonCnt: "integer",
              betsAdjustedCnt: "integer",
              revenueAmt: "decimal",
              networkAdjustAmt: "decimal",
              taxPlayerIncomeAmt: "decimal",
              fundsType: "string | has_specifics",
            };
          }

          if (
            selectedCategories.some(
              (cat) => cat.categoryId === "RRI_pariMutuel",
            )
          ) {
            gameSummaryDetail.pariMutuelSummary = {
              stakeAmt: "decimal",
              freeStakeAmt: "decimal",
              refundAmt: "decimal",
              freeRefundAmt: "decimal",
              baseWinAmt: "decimal",
              adjustmentAmt: "decimal",
              betsPlacedCnt: "integer",
              betsRefundedCnt: "integer",
              betsWonCnt: "integer",
              betsAdjustedCnt: "integer",
              revenueAmt: "decimal",
              networkAdjustAmt: "decimal",
              taxPlayerIncomeAmt: "decimal",
              slipsExpiredCnt: "integer",
              slipsExpiredAmt: "decimal",
              fundsType: "string | has_specifics",
            };
          }

          if (
            selectedCategories.some((cat) => cat.categoryId === "RRI_pokerCash")
          ) {
            gameSummaryDetail.pokerCashSummary = {
              playerCnt: "integer",
              freePlayerCnt: "integer",
              stakeAmt: "decimal",
              freeStakeAmt: "decimal",
              refundAmt: "decimal",
              freeRefundAmt: "decimal",
              wagerAmt: "decimal",
              baseWinAmt: "decimal",
              jackpotContribAmt: "decimal",
              jackpotWinAmt: "decimal",
              rakeAmt: "decimal",
              gamesPlayedCnt: "integer",
              cashOutAmt: "decimal",
              revenueAmt: "decimal",
              networkAdjustAmt: "decimal",
              taxPlayerIncomeAmt: "decimal",
              fundsType: "string | has_specifics",
            };
          }

          if (
            selectedCategories.some(
              (cat) => cat.categoryId === "RRI_pokerTourn",
            )
          ) {
            gameSummaryDetail.pokerTournSummary = {
              playerCnt: "integer",
              freePlayerCnt: "integer",
              stakeAmt: "decimal",
              freeStakeAmt: "decimal",
              refundAmt: "decimal",
              freeRefundAmt: "decimal",
              rakeAmt: "decimal",
              baseWinAmt: "decimal",
              jackpotContribAmt: "decimal",
              jackpotWinAmt: "decimal",
              fromPotAmt: "decimal",
              potContribAmt: "decimal",
              potAdditionalAmt: "decimal",
              toPotAmt: "decimal",
              revenueAmt: "decimal",
              networkAdjustAmt: "decimal",
              taxPlayerIncomeAmt: "decimal",
              fundsType: "string | has_specifics",
            };
          }

          if (
            selectedCategories.some((cat) => cat.categoryId === "RRI_fantasy")
          ) {
            gameSummaryDetail.fantasySummary = {
              playerCnt: "integer",
              freePlayerCnt: "integer",
              stakeAmt: "decimal",
              freeStakeAmt: "decimal",
              refundAmt: "decimal",
              freeRefundAmt: "decimal",
              rakeAmt: "decimal",
              baseWinAmt: "decimal",
              fromPotAmt: "decimal",
              potContribAmt: "decimal",
              potAdditionalAmt: "decimal",
              toPotAmt: "decimal",
              revenueAmt: "decimal",
              networkAdjustAmt: "decimal",
              taxPlayerIncomeAmt: "decimal",
              fundsType: "string | has_specifics",
            };
          }

          if (
            selectedCategories.some((cat) => cat.categoryId === "RRI_bingo")
          ) {
            gameSummaryDetail.bingoSummary = {
              bingoId: "string",
              roundId: "string",
              playerCnt: "integer",
              freePlayerCnt: "integer",
              stakeAmt: "decimal",
              freeStakeAmt: "decimal",
              refundAmt: "decimal",
              freeRefundAmt: "decimal",
              rakeAmt: "decimal",
              baseWinAmt: "decimal",
              jackpotContribAmt: "decimal",
              jackpotWinAmt: "decimal",
              fromPotAmt: "decimal",
              potContribAmt: "decimal",
              potAdditionalAmt: "decimal",
              toPotAmt: "decimal",
              revenueAmt: "decimal",
              networkAdjustAmt: "decimal",
              taxPlayerIncomeAmt: "decimal",
              issuedCardCnt: "integer",
              returnedCardCnt: "integer",
              fundsType: "string | has_specifics",
            };
          }

          // Add land-based fixed odds specific structure if selected
          if (hasLandFixedOdds) {
            gameSummaryDetail.landFixedOddsSummary = {
              stakeAmt: "decimal",
              freeStakeAmt: "decimal",
              refundAmt: "decimal",
              freeRefundAmt: "decimal",
              forfeitAmt: "decimal",
              baseWinAmt: "decimal",
              adjustmentAmt: "decimal",
              betsPlacedCnt: "integer",
              betsRefundedCnt: "integer",
              betsWonCnt: "integer",
              betsAdjustedCnt: "integer",
              revenueAmt: "decimal",
              networkAdjustAmt: "decimal",
              taxPlayerIncomeAmt: "decimal",
              slipsExpiredCnt: "integer",
              slipsExpiredAmt: "decimal",
              fundsType: "string | has_specifics",
              terminalId: "string",
              location: "string",
            };
          }

          const baseStructure = {
            gameSummary: [
              {
                brandId: "string",
                game: {
                  gameId: "string",
                  gameName: "string",
                  gameCategory: "string",
                  gameType: "string | has_specifics",
                  gameVariant: "string|null",
                },
                gameSummaryDetail: gameSummaryDetail,
                gameMovementList: [
                  {
                    transClass: "string | has_specifics",
                    transType: "string | has_specifics",
                    fundsType: "string | has_specifics",
                    transAmt: "decimal",
                  },
                ],
              },
            ],
          };

          let result = JSON.stringify(baseStructure, null, 2);

          result = result.replace(
            '"gameSummaryDetail": {',
            '\n // gameSummaryDetail will include different metrics based on selected game categories. \n // Only include relevant sections for the selected category in a record.\n  "gameSummaryDetail": {',
          );
          return result;
        },

        game_funds_in_play: () => {
          const selectedCategories =
            this.getSelectedCategoriesForReport("game_funds_in_play");
          const hasLandFixedOdds = selectedCategories.some(
            (cat) => cat.categoryId === "RRI_landFixedOdds",
          );

          // Build gameInPlayDetail dynamically based on selected categories
          const gameInPlayDetail = {};

          if (
            selectedCategories.some(
              (cat) => cat.categoryId === "RRI_casinoGame",
            )
          ) {
            gameInPlayDetail.casinoGameInPlay = {
              stakeAmt: "decimal",
              freeStakeAmt: "decimal",
              refundAmt: "decimal",
              freeRefundAmt: "decimal",
              wagerAmt: "decimal",
              baseWinAmt: "decimal",
              jackpotContribAmt: "decimal",
              jackpotWinAmt: "decimal",
              gamesPlayedCnt: "integer",
              fundsType: "string | has_specifics",
            };
          }

          if (
            selectedCategories.some(
              (cat) => cat.categoryId === "RRI_casinoTourn",
            )
          ) {
            gameInPlayDetail.casinoTournInPlay = {
              playerCnt: "integer",
              freePlayerCnt: "integer",
              stakeAmt: "decimal",
              freeStakeAmt: "decimal",
              refundAmt: "decimal",
              freeRefundAmt: "decimal",
              rakeAmt: "decimal",
              baseWinAmt: "decimal",
              fromPotAmt: "decimal",
              potContribAmt: "decimal",
              potAdditionalAmt: "decimal",
              toPotAmt: "decimal",
              fundsType: "string | has_specifics",
            };
          }

          if (
            selectedCategories.some((cat) => cat.categoryId === "RRI_fixedOdds")
          ) {
            gameInPlayDetail.fixedOddsInPlay = {
              stakeAmt: "decimal",
              freeStakeAmt: "decimal",
              refundAmt: "decimal",
              freeRefundAmt: "decimal",
              forfeitAmt: "decimal",
              baseWinAmt: "decimal",
              adjustmentAmt: "decimal",
              betsPlacedCnt: "integer",
              betsRefundedCnt: "integer",
              betsWonCnt: "integer",
              betsAdjustedCnt: "integer",
              slipIssuedCnt: "integer",
              slipUnclaimedCnt: "integer",
              slipUnclaimedAmt: "decimal",
              fundsType: "string | has_specifics",
            };
          }

          if (
            selectedCategories.some(
              (cat) => cat.categoryId === "RRI_betExchange",
            )
          ) {
            gameInPlayDetail.exchangeInPlay = {
              stakeAmt: "decimal",
              freeStakeAmt: "decimal",
              refundAmt: "decimal",
              freeRefundAmt: "decimal",
              baseWinAmt: "decimal",
              rakeAmt: "decimal",
              adjustmentAmt: "decimal",
              betsPlacedCnt: "integer",
              betsRefundedCnt: "integer",
              betsWonCnt: "integer",
              betsAdjustedCnt: "integer",
              fundsType: "string | has_specifics",
            };
          }

          if (
            selectedCategories.some(
              (cat) => cat.categoryId === "RRI_pariMutuel",
            )
          ) {
            gameInPlayDetail.pariMutuelInPlay = {
              stakeAmt: "decimal",
              freeStakeAmt: "decimal",
              refundAmt: "decimal",
              freeRefundAmt: "decimal",
              baseWinAmt: "decimal",
              adjustmentAmt: "decimal",
              betsPlacedCnt: "integer",
              betsRefundedCnt: "integer",
              betsWonCnt: "integer",
              betsAdjustedCnt: "integer",
              slipIssuedCnt: "integer",
              slipUnclaimedCnt: "integer",
              slipUnclaimedAmt: "decimal",
              fundsType: "string | has_specifics",
            };
          }

          if (
            selectedCategories.some((cat) => cat.categoryId === "RRI_pokerCash")
          ) {
            gameInPlayDetail.pokerCashInPlay = {
              playerCnt: "integer",
              freePlayerCnt: "integer",
              stakeAmt: "decimal",
              freeStakeAmt: "decimal",
              refundAmt: "decimal",
              freeRefundAmt: "decimal",
              wagerAmt: "decimal",
              baseWinAmt: "decimal",
              jackpotContribAmt: "decimal",
              jackpotWinAmt: "decimal",
              rakeAmt: "decimal",
              gamesPlayedCnt: "integer",
              cashOutAmt: "decimal",
              fundsType: "string | has_specifics",
            };
          }

          if (
            selectedCategories.some(
              (cat) => cat.categoryId === "RRI_pokerTourn",
            )
          ) {
            gameInPlayDetail.pokerTournInPlay = {
              playerCnt: "integer",
              freePlayerCnt: "integer",
              stakeAmt: "decimal",
              freeStakeAmt: "decimal",
              refundAmt: "decimal",
              freeRefundAmt: "decimal",
              rakeAmt: "decimal",
              baseWinAmt: "decimal",
              jackpotContribAmt: "decimal",
              jackpotWinAmt: "decimal",
              fromPotAmt: "decimal",
              potContribAmt: "decimal",
              potAdditionalAmt: "decimal",
              toPotAmt: "decimal",
              fundsType: "string | has_specifics",
            };
          }

          if (
            selectedCategories.some((cat) => cat.categoryId === "RRI_fantasy")
          ) {
            gameInPlayDetail.fantasyInPlay = {
              playerCnt: "integer",
              freePlayerCnt: "integer",
              stakeAmt: "decimal",
              freeStakeAmt: "decimal",
              refundAmt: "decimal",
              freeRefundAmt: "decimal",
              rakeAmt: "decimal",
              baseWinAmt: "decimal",
              fromPotAmt: "decimal",
              potContribAmt: "decimal",
              potAdditionalAmt: "decimal",
              toPotAmt: "decimal",
              fundsType: "string | has_specifics",
            };
          }

          if (
            selectedCategories.some((cat) => cat.categoryId === "RRI_bingo")
          ) {
            gameInPlayDetail.bingoInPlay = {
              bingoId: "string",
              roundId: "string",
              playerCnt: "integer",
              freePlayerCnt: "integer",
              stakeAmt: "decimal",
              freeStakeAmt: "decimal",
              refundAmt: "decimal",
              freeRefundAmt: "decimal",
              rakeAmt: "decimal",
              baseWinAmt: "decimal",
              jackpotContribAmt: "decimal",
              jackpotWinAmt: "decimal",
              fromPotAmt: "decimal",
              potContribAmt: "decimal",
              potAdditionalAmt: "decimal",
              toPotAmt: "decimal",
              fundsType: "string | has_specifics",
            };
          }

          // Add land-based fixed odds specific structure if selected
          if (hasLandFixedOdds) {
            gameInPlayDetail.landFixedOddsInPlay = {
              stakeAmt: "decimal",
              freeStakeAmt: "decimal",
              refundAmt: "decimal",
              freeRefundAmt: "decimal",
              forfeitAmt: "decimal",
              baseWinAmt: "decimal",
              adjustmentAmt: "decimal",
              betsPlacedCnt: "integer",
              betsRefundedCnt: "integer",
              betsWonCnt: "integer",
              betsAdjustedCnt: "integer",
              slipIssuedCnt: "integer",
              slipUnclaimedCnt: "integer",
              slipUnclaimedAmt: "decimal",
              fundsType: "string | has_specifics",
              terminalId: "string",
              location: "string",
            };
          }

          const baseStructure = {
            gameFundsInPlay: [
              {
                periodStart: "date ",
                statusDateTime: "datetime",
                brandId: "string ",
                game: {
                  gameId: "string",
                  gameName: "string",
                  gameCategory: "string ",
                  gameType: "string | has_specifics",
                  gameVariant: "string | null",
                },
                gameInPlayDetail: gameInPlayDetail,
                gameMovementList: [
                  {
                    transClass: "string | has_specifics",
                    transType: "string | has_specifics",
                    fundsType: "string | has_specifics",
                    transAmt: "decimal",
                  },
                ],
              },
            ],
          };

          let result = JSON.stringify(baseStructure, null, 2);

          result = result.replace(
            '"gameInPlayDetail": {',
            '\n // gameInPlayDetail will include different metrics based on selected game categories. \n // Only include relevant sections for the selected category in a record.\n  "gameInPlayDetail": {',
          );

          return result;
        },

        player_summary: () =>
          JSON.stringify(
            {
              playerSummary: [
                {
                  brandId: "string",
                  player: {
                    playerId: "string",
                  },
                  playerGameSummary: [
                    {
                      game: {
                        gameId: "string",
                        gameName: "string",
                        gameCategory: "string",
                        gameType: "string | has_specifics",
                        gameVariant: "string|null",
                      },
                      playerMovementList: [
                        {
                          transType: "string",
                          fundsType: "string | has_specifics",
                          transAmt: "decimal",
                        },
                      ],
                    },
                  ],
                },
              ],
            },
            null,
            2,
          ),

        player_registration: () => {
          let result = JSON.stringify(
            {
              playerRegistration: [
                {
                  brandId:
                    "string | This value is got from your operator NCEMS portal.",
                  transDateTime: "datetime",
                  player: {
                    playerId:
                      "string | Got the operator's CMS. Should be unique for each player & consistent across reports.",
                  },
                  playerInfo: {
                    givenNames: "string",
                    familyNames: "string",
                    nationality: "string",
                    birthDate: "date",
                    genderType: "string | has_specifics",
                    fromDateTime: "datetime",
                  },
                  playerStatusInfo: {
                    playerStatus: "string | has_specifics",
                    operatorStatus: "string",
                    fromDateTime: "datetime",
                    verifiedDateTime: "datetime",
                  },
                  playerPhoneList: [
                    {
                      phoneCountry: "string",
                      phoneNumber: "string",
                      verifiedDateTime: "datetime",
                      isDeleted: "boolean | has_specifics",
                      fromDateTime: "datetime",
                    },
                  ],
                  playerIdentificationList: [
                    {
                      idType: "string | has_specifics",
                      countryCode: "string",
                      subDivision: "string | has_specifics",
                      idNumber: "string",
                      issueDate: "date",
                      expirationDate: "date",
                      isDeleted: "boolean | has_specifics",
                      fromDateTime: "datetime",
                    },
                  ],

                  playerExclusionList: [
                    {
                      exclusionSource: "string | has_specifics",
                      startDate: "datetime",
                      endDate: "datetime",
                      fromTime: "string",
                      toTime: "string",
                      autoContinuance: "boolean | has_specifics",
                      fromDateTime: "datetime",
                    },
                  ],
                },
              ],
            },
            null,
            2,
          );

          result = result.replace(
            '"playerIdentificationList": [',
            '\n   // This playerIdentificationList is optional. An empty list can be provided,\n       "playerIdentificationList": [',
          );

          result = result.replace(
            '"playerExclusionList": [',
            '\n   // This playerExclusionList is optional. An empty list can be provided,\n       "playerExclusionList": [',
          );

          return result;
        },

        player_activity: () => {
          let result = JSON.stringify(
            {
              playerActivity: [
                {
                  brandId:
                    "string | This value is got from your operator NCEMS portal.",
                  player: {
                    playerId:
                      "string | Got the operator's CMS. Should be unique for each player & consistent across reports.",
                  },
                  playerActivityDetail: {
                    playerActivity: "string",
                    playerActivityDesc:
                      "string | null: ppc:<PaymentServiceProviderCode>;tid:<ExternalTransID> e.g ppc:VISA;tid:TID-123456",
                    playerActivityDateTime: "datetime",

                    transferee: {
                      operatorId: "string",
                      externalTransId: "string",
                      operatorType: "string | has_specifics",
                    },
                  },
                  startingBalanceList: [
                    {
                      fundsType: "string",
                      startingBalanceAmt: "decimal",
                    },
                  ],
                  playerMovementList: [
                    {
                      transType: "string | has_specifics",
                      fundsType: "string | has_specifics",
                      transAmt: "decimal",
                    },
                  ],
                  endingBalanceList: [
                    {
                      fundsType: "string | has_specifics",
                      endingBalanceAmt: "decimal",
                    },
                  ],
                },
              ],
            },
            null,
            2,
          );

          result = result.replace(
            '"transferee": {',
            '\n   // transferee is optional. Can be excluded \n       "transferee": {',
          );

          result = result.replace(
            '"startingBalanceList": [',
            '\n   // startingBalanceList is optional. An empty list can be provided,\n       "startingBalanceList": [',
          );

          result = result.replace(
            '"playerMovementList": [',
            '\n   // playerMovementList is optional. An empty list can be provided,\n       "playerMovementList": [',
          );

          result = result.replace(
            '"endingBalanceList": [',
            '\n   // endingBalanceList is optional. An empty list can be provided,\n       "endingBalanceList": [',
          );

          return result;
        },

        land_event: () => {
          let result = JSON.stringify(
            {
              landEvent: [
                {
                  brandId: "string",
                  terminal: {
                    terminalId: "string | Got from operator NCEMS portal",
                  },
                  event: {
                    eventId: "string",
                    eventCode: "string | has_specifics",
                    eventText: "string | null",
                    eventDateTime: "datetime",
                  },
                  associatedData: {
                    voucherData: {
                      transPeriod: "string",
                      transactionId: "string",
                      validationId: "string",
                      voucherAmt: "decimal",
                      fundsType: "string",
                    },
                    gameOutcomeData: {
                      transPeriod: "string",
                      transactionId: "string",
                      bonusWinAmt: "decimal",
                      jackpotWinAmt: "decimal",
                      largeWin: "boolean",
                      controllerId: "string",
                      levelId: "string",
                    },
                    handpayDisbursementData: {
                      transPeriod: "string",
                      transactionId: "string",
                      cashAmt: "decimal",
                      handpayType: "string",
                    },
                    noteAcceptedData: {
                      transPeriod: "string",
                      transactionId: "string",
                      noteAmt: "decimal",
                    },
                    watData: {
                      transactionId: "string",
                      cardIdentifier: "string",
                      transferAmt: "decimal",
                      fundsType: "string",
                      watType: "string",
                    },
                  },
                },
              ],
            },
            null,
            2,
          );

          result = result.replace(
            '"voucherData": {',
            '\n   // voucherData is required for RRI_voucherIssued & RRI_voucherRedeemed eventCodes. \n       "voucherData": {',
          );

          result = result.replace(
            '"gameOutcomeData": {',
            '\n   // gameOutcomeData is required for RRI_gameOutcome & RRI_progressiveAward eventCodes. \n       "gameOutcomeData": {',
          );

          result = result.replace(
            '"handpayDisbursementData": {',
            '\n   // handpayDisbursementData is required for RRI_handpayDisbursement eventCode. \n       "handpayDisbursementData": {',
          );

          result = result.replace(
            '"noteAcceptedData": {',
            '\n   // noteAcceptedData is required for RRI_noteAccepted eventCode. \n       "noteAcceptedData": {',
          );

          result = result.replace(
            '"watData": {',
            '\n   // watData is required for RRI_watCompleted eventCode. \n       "watData": {',
          );

          return result;
        },

        jackpot_balance: () =>
          JSON.stringify(
            {
              jackpotBalance: [
                {
                  statusDateTime: "datetime",
                  brandId: "string",
                  controller: {
                    serverId: "string",
                    controllerId: "string",
                  },
                  jackpotOpeningList: [
                    {
                      serverId: "string",
                      controllerId: "string",
                      levelId: "string",
                      levelName: "string",
                      levelInstance: "string",
                      balanceAmt: "decimal",
                      overflowAmt: "decimal",
                      resetAmt: "decimal",
                      ltdWagerAmt: "decimal",
                      ltdContribAmt: "decimal",
                      ltdOffsetAmt: "decimal",
                      ltdResetAmt: "decimal",
                      ltdAdjustAmt: "decimal",
                      ltdAwardAmt: "decimal",
                    },
                  ],
                  jackpotGameList: [
                    {
                      game: {
                        gameId: "string",
                        gameName: "string",
                        gameCategory: "string",
                        gameType: "string | has_specifics",
                        gameVariant: "string|null",
                      },
                      jackpotMovementList: [
                        {
                          transType: "string",
                          fundsType: "string",
                          transAmt: "decimal",
                        },
                      ],
                    },
                  ],
                  jackpotActivityList: [
                    {
                      transType: "string | has_specifics",
                      fundsType: "string | has_specifics",
                      transAmt: "decimal",
                    },
                  ],
                  jackpotClosingList: [
                    {
                      serverId: "string",
                      controllerId: "string",
                      levelId: "string",
                      levelName: "string",
                      levelInstance: "string",
                      balanceAmt: "decimal",
                      overflowAmt: "decimal",
                      resetAmt: "decimal",
                      ltdWagerAmt: "decimal",
                      ltdContribAmt: "decimal",
                      ltdOffsetAmt: "decimal",
                      ltdResetAmt: "decimal",
                      ltdAdjustAmt: "decimal",
                      ltdAwardAmt: "decimal",
                    },
                  ],
                },
              ],
            },
            null,
            2,
          ),
      };

      if (jsonStructureGenerators[reportId]) {
        return jsonStructureGenerators[reportId]();
      }

      return getDefaultStructure();
    },

    getReportXmlStructure(reportId) {
      const createXmlTag = (
        tagName,
        value,
        indent = 0,
        selfClosing = false,
        attributes = {},
      ) => {
        const indentStr = "  ".repeat(indent);

        if (selfClosing) {
          if (Object.keys(attributes).length > 0) {
            const attrs = Object.entries(attributes)
              .map(([key, val]) => `${key}="${val}"`)
              .join(" ");
            return `${indentStr}<${tagName} ${attrs} />`;
          }
          return `${indentStr}<${tagName} />`;
        }

        if (value === undefined) {
          return `${indentStr}<${tagName}>`;
        }

        return `${indentStr}<${tagName}>${value}</${tagName}>`;
      };

      const getDefaultXmlStructure = () => {
        const selectedCategories =
          this.getSelectedCategoriesForReport(reportId);
        const xmlLines = ["<data>"];
        xmlLines.push("  <reportConfiguration>");
        xmlLines.push(createXmlTag("reportId", reportId, 4));
        xmlLines.push("    <gameCategories>");

        selectedCategories.forEach((cat) => {
          const category = this.getAllCategories().find(
            (c) => c.id === cat.categoryId,
          );
          xmlLines.push("      <gameCategory>");
          xmlLines.push(createXmlTag("categoryId", cat.categoryId, 8));
          xmlLines.push(
            createXmlTag(
              "categoryName",
              category ? category.name : cat.categoryId,
              8,
            ),
          );
          xmlLines.push(
            createXmlTag(
              "categoryValue",
              category ? category.value : cat.categoryId,
              8,
            ),
          );
          xmlLines.push("      </gameCategory>");
        });

        if (selectedCategories.length === 0) {
          xmlLines.push(createXmlTag("gameCategories", "None", 6));
        }
        xmlLines.push("    </gameCategories>");
        xmlLines.push("  </reportConfiguration>");
        xmlLines.push("</data>");
        return xmlLines.join("\n");
      };

      const xmlStructureGenerators = {
        terminal_config: () => {
          const selectedCategories =
            this.getSelectedCategoriesForReport("terminal_config");
          const hasLandSlot = selectedCategories.some(
            (cat) => cat.categoryId === "RRI_landSlot",
          );
          const hasLandTable = selectedCategories.some(
            (cat) => cat.categoryId === "RRI_landTable",
          );

          const xmlLines = ["<data>"];
          xmlLines.push("  <terminalConfig>");
          xmlLines.push(
            createXmlTag(
              "brandId",
              "string | Got from operator NCEMS portal",
              4,
            ),
          );

          xmlLines.push("    <terminal>");
          xmlLines.push(createXmlTag("terminalId", "string", 6));
          xmlLines.push("    </terminal>");

          xmlLines.push("    <model>");
          xmlLines.push(createXmlTag("modelId", "string | has_specifics", 6));
          xmlLines.push("    </model>");

          xmlLines.push("    <terminalConfigDetail>");
          xmlLines.push(createXmlTag("terminalName", "string", 6));
          xmlLines.push(createXmlTag("regulatorId", "string", 6));
          xmlLines.push(createXmlTag("serialNum", "string", 6));
          xmlLines.push(
            createXmlTag("isDeleted", "boolean | has_specifics", 6),
          );
          xmlLines.push(createXmlTag("fromDateTime", "datetime", 6));
          xmlLines.push(createXmlTag("toDateTime", "datetime", 6));

          // Add slotTerminal for landSlot category
          if (hasLandSlot) {
            xmlLines.push(
              "      <!-- Only for landSlot or EGMs category, include slotTerminal details: -->",
            );
            xmlLines.push("      <slotTerminal>");
            xmlLines.push(createXmlTag("egmId", "string", 8));
            xmlLines.push(createXmlTag("machineNum", "string", 8));
            xmlLines.push(createXmlTag("machineType", "string", 8));
            xmlLines.push(createXmlTag("yearMade", "string", 8));
            xmlLines.push("      </slotTerminal>");

            xmlLines.push(
              "      <!-- For Roulette tables, add rouletteWheel object in terminalConfigDetail -->",
            );
            xmlLines.push("      <rouletteWheel>");
            xmlLines.push(createXmlTag("mfgName", "string", 8));
            xmlLines.push(createXmlTag("yearMade", "string", 8));
            xmlLines.push(createXmlTag("cylinderId", "string", 8));
            xmlLines.push("      </rouletteWheel>");
          }

          xmlLines.push("    </terminalConfigDetail>");

          // Add installedGameList for landTable category
          if (hasLandTable) {
            xmlLines.push(
              "    <!-- Only for landTable category, include installedGameList with game details: -->",
            );
            xmlLines.push("    <installedGameList>");
            xmlLines.push("      <installedGame>");
            xmlLines.push("        <game>");
            xmlLines.push(createXmlTag("gameId", "string", 10));
            xmlLines.push(createXmlTag("gameName", "string", 10));
            xmlLines.push(
              createXmlTag("gameCategory", "string | has_specifics", 10),
            );
            xmlLines.push(
              createXmlTag("gameType", "string | has_specifics", 10),
            );
            xmlLines.push("        </game>");
            xmlLines.push("      </installedGame>");
            xmlLines.push("    </installedGameList>");
          }

          xmlLines.push("  </terminalConfig>");
          xmlLines.push("</data>");
          return xmlLines.join("\n");
        },

        land_summary: () => {
          const selectedCategories =
            this.getSelectedCategoriesForReport("land_summary");
          const hasLandSlot = selectedCategories.some(
            (cat) => cat.categoryId === "RRI_landSlot",
          );
          const hasLandTable = selectedCategories.some(
            (cat) => cat.categoryId === "RRI_landTable",
          );

          const xmlLines = ["<data>"];
          xmlLines.push("  <landSummary>");
          xmlLines.push(
            createXmlTag(
              "brandId",
              "string | Got from operator NCEMS portal",
              4,
            ),
          );

          xmlLines.push("    <terminal>");
          xmlLines.push(createXmlTag("terminalId", "string", 6));
          xmlLines.push("    </terminal>");

          // Add movementList and meterList for landSlot category
          if (hasLandSlot) {
            xmlLines.push(
              "    <!-- Only for landSlot category, include movementList with transaction details: -->",
            );
            xmlLines.push("    <movementList>");
            xmlLines.push(createXmlTag("startDateTime", "datetime", 6));
            xmlLines.push(createXmlTag("endDateTime", "datetime", 6));
            xmlLines.push("      <movements>");
            xmlLines.push(
              createXmlTag("transClass", "string | has_specifics", 8),
            );
            xmlLines.push(
              createXmlTag("transType", "string | has_specifics", 8),
            );
            xmlLines.push(createXmlTag("movementValue", "decimal", 8));
            xmlLines.push("      </movements>");
            xmlLines.push("    </movementList>");

            xmlLines.push(
              "    <!-- Only for landSlot category, include meterList with meter details: -->",
            );
            xmlLines.push("    <meterList>");
            xmlLines.push(createXmlTag("statusDateTime", "datetime", 6));
            xmlLines.push("      <meters>");
            xmlLines.push(
              createXmlTag("transClass", "string | has_specifics", 8),
            );
            xmlLines.push(
              createXmlTag("transType", "string | has_specifics", 8),
            );
            xmlLines.push(createXmlTag("meterValue", "decimal", 8));
            xmlLines.push("      </meters>");
            xmlLines.push("    </meterList>");
          }

          // Add landGameList for landTable category
          if (hasLandTable) {
            xmlLines.push(
              "    <!-- Only for landTable category, include landGameList with game and movement details: -->",
            );
            xmlLines.push("    <landGameList>");
            xmlLines.push("      <landGame>");
            xmlLines.push("        <game>");
            xmlLines.push(createXmlTag("gameId", "string", 10));
            xmlLines.push(createXmlTag("gameName", "string", 10));
            xmlLines.push(
              createXmlTag("gameType", "string | has_specifics", 10),
            );
            xmlLines.push("        </game>");
            xmlLines.push("        <gameMovementList>");
            xmlLines.push(createXmlTag("startDateTime", "datetime", 10));
            xmlLines.push(createXmlTag("endDateTime", "datetime", 10));
            xmlLines.push("          <movements>");
            xmlLines.push(
              createXmlTag("transClass", "string | has_specifics", 12),
            );
            xmlLines.push(
              createXmlTag("transType", "string | has_specifics", 12),
            );
            xmlLines.push(createXmlTag("movementValue", "decimal", 12));
            xmlLines.push("          </movements>");
            xmlLines.push("        </gameMovementList>");
            xmlLines.push("      </landGame>");
            xmlLines.push("    </landGameList>");
          }

          xmlLines.push("  </landSummary>");
          xmlLines.push("</data>");
          return xmlLines.join("\n");
        },

        game_summary: () => {
          const selectedCategories =
            this.getSelectedCategoriesForReport("game_summary");

          const xmlLines = ["<data>"];
          xmlLines.push("  <gameSummary>");
          xmlLines.push(createXmlTag("brandId", "string", 4));

          xmlLines.push("    <game>");
          xmlLines.push(createXmlTag("gameId", "string", 6));
          xmlLines.push(createXmlTag("gameName", "string", 6));
          xmlLines.push(createXmlTag("gameCategory", "string", 6));
          xmlLines.push(createXmlTag("gameType", "string | has_specifics", 6));
          xmlLines.push(createXmlTag("gameVariant", "string|null", 6));
          xmlLines.push("    </game>");

          xmlLines.push(
            "    <!-- gameSummaryDetail will include different metrics based on selected game categories. -->",
          );
          xmlLines.push(
            "    <!-- Only include relevant sections for the selected category in a record. -->",
          );
          xmlLines.push("    <gameSummaryDetail>");

          // Add casinoGameSummary section if RRI_casinoGame is selected
          if (
            selectedCategories.some(
              (cat) => cat.categoryId === "RRI_casinoGame",
            )
          ) {
            xmlLines.push("      <casinoGameSummary>");
            xmlLines.push(createXmlTag("playerCnt", "integer", 8));
            xmlLines.push(createXmlTag("freePlayerCnt", "integer", 8));
            xmlLines.push(createXmlTag("stakeAmt", "decimal", 8));
            xmlLines.push(createXmlTag("freeStakeAmt", "decimal", 8));
            xmlLines.push(createXmlTag("refundAmt", "decimal", 8));
            xmlLines.push(createXmlTag("freeRefundAmt", "decimal", 8));
            xmlLines.push(createXmlTag("wagerAmt", "decimal", 8));
            xmlLines.push(createXmlTag("baseWinAmt", "decimal", 8));
            xmlLines.push(createXmlTag("jackpotContribAmt", "decimal", 8));
            xmlLines.push(createXmlTag("jackpotWinAmt", "decimal", 8));
            xmlLines.push(createXmlTag("gamesPlayedCnt", "integer", 8));
            xmlLines.push(createXmlTag("revenueAmt", "decimal", 8));
            xmlLines.push(createXmlTag("networkAdjustAmt", "decimal", 8));
            xmlLines.push(createXmlTag("taxPlayerIncomeAmt", "decimal", 8));
            xmlLines.push(
              createXmlTag("fundsType", "string | has_specifics", 8),
            );
            xmlLines.push("      </casinoGameSummary>");
          }

          // Add casinoTournSummary section if RRI_casinoTourn is selected
          if (
            selectedCategories.some(
              (cat) => cat.categoryId === "RRI_casinoTourn",
            )
          ) {
            xmlLines.push("      <casinoTournSummary>");
            xmlLines.push(createXmlTag("playerCnt", "integer", 8));
            xmlLines.push(createXmlTag("freePlayerCnt", "integer", 8));
            xmlLines.push(createXmlTag("stakeAmt", "decimal", 8));
            xmlLines.push(createXmlTag("freeStakeAmt", "decimal", 8));
            xmlLines.push(createXmlTag("refundAmt", "decimal", 8));
            xmlLines.push(createXmlTag("freeRefundAmt", "decimal", 8));
            xmlLines.push(createXmlTag("rakeAmt", "decimal", 8));
            xmlLines.push(createXmlTag("baseWinAmt", "decimal", 8));
            xmlLines.push(createXmlTag("fromPotAmt", "decimal", 8));
            xmlLines.push(createXmlTag("potContribAmt", "decimal", 8));
            xmlLines.push(createXmlTag("potAdditionalAmt", "decimal", 8));
            xmlLines.push(createXmlTag("toPotAmt", "decimal", 8));
            xmlLines.push(createXmlTag("revenueAmt", "decimal", 8));
            xmlLines.push(createXmlTag("networkAdjustAmt", "decimal", 8));
            xmlLines.push(createXmlTag("taxPlayerIncomeAmt", "decimal", 8));
            xmlLines.push(
              createXmlTag("fundsType", "string | has_specifics", 8),
            );
            xmlLines.push("      </casinoTournSummary>");
          }

          // Add fixedOddsSummary section if RRI_fixedOdds is selected
          if (
            selectedCategories.some((cat) => cat.categoryId === "RRI_fixedOdds")
          ) {
            xmlLines.push("      <fixedOddsSummary>");
            xmlLines.push(createXmlTag("stakeAmt", "decimal", 8));
            xmlLines.push(createXmlTag("freeStakeAmt", "decimal", 8));
            xmlLines.push(createXmlTag("refundAmt", "decimal", 8));
            xmlLines.push(createXmlTag("freeRefundAmt", "decimal", 8));
            xmlLines.push(createXmlTag("forfeitAmt", "decimal", 8));
            xmlLines.push(createXmlTag("baseWinAmt", "decimal", 8));
            xmlLines.push(createXmlTag("adjustmentAmt", "decimal", 8));
            xmlLines.push(createXmlTag("betsPlacedCnt", "integer", 8));
            xmlLines.push(createXmlTag("betsRefundedCnt", "integer", 8));
            xmlLines.push(createXmlTag("betsWonCnt", "integer", 8));
            xmlLines.push(createXmlTag("betsAdjustedCnt", "integer", 8));
            xmlLines.push(createXmlTag("revenueAmt", "decimal", 8));
            xmlLines.push(createXmlTag("networkAdjustAmt", "decimal", 8));
            xmlLines.push(createXmlTag("taxPlayerIncomeAmt", "decimal", 8));
            xmlLines.push(createXmlTag("slipsExpiredCnt", "integer", 8));
            xmlLines.push(createXmlTag("slipsExpiredAmt", "decimal", 8));
            xmlLines.push(
              createXmlTag("fundsType", "string | has_specifics", 8),
            );
            xmlLines.push("      </fixedOddsSummary>");
          }

          // Add exchangeSummary section if RRI_betExchange is selected
          if (
            selectedCategories.some(
              (cat) => cat.categoryId === "RRI_betExchange",
            )
          ) {
            xmlLines.push("      <exchangeSummary>");
            xmlLines.push(createXmlTag("stakeAmt", "decimal", 8));
            xmlLines.push(createXmlTag("freeStakeAmt", "decimal", 8));
            xmlLines.push(createXmlTag("refundAmt", "decimal", 8));
            xmlLines.push(createXmlTag("freeRefundAmt", "decimal", 8));
            xmlLines.push(createXmlTag("baseWinAmt", "decimal", 8));
            xmlLines.push(createXmlTag("rakeAmt", "decimal", 8));
            xmlLines.push(createXmlTag("adjustmentAmt", "decimal", 8));
            xmlLines.push(createXmlTag("betsPlacedCnt", "integer", 8));
            xmlLines.push(createXmlTag("betsRefundedCnt", "integer", 8));
            xmlLines.push(createXmlTag("betsWonCnt", "integer", 8));
            xmlLines.push(createXmlTag("betsAdjustedCnt", "integer", 8));
            xmlLines.push(createXmlTag("revenueAmt", "decimal", 8));
            xmlLines.push(createXmlTag("networkAdjustAmt", "decimal", 8));
            xmlLines.push(createXmlTag("taxPlayerIncomeAmt", "decimal", 8));
            xmlLines.push(
              createXmlTag("fundsType", "string | has_specifics", 8),
            );
            xmlLines.push("      </exchangeSummary>");
          }

          // Add pariMutuelSummary section if RRI_pariMutuel is selected
          if (
            selectedCategories.some(
              (cat) => cat.categoryId === "RRI_pariMutuel",
            )
          ) {
            xmlLines.push("      <pariMutuelSummary>");
            xmlLines.push(createXmlTag("stakeAmt", "decimal", 8));
            xmlLines.push(createXmlTag("freeStakeAmt", "decimal", 8));
            xmlLines.push(createXmlTag("refundAmt", "decimal", 8));
            xmlLines.push(createXmlTag("freeRefundAmt", "decimal", 8));
            xmlLines.push(createXmlTag("baseWinAmt", "decimal", 8));
            xmlLines.push(createXmlTag("adjustmentAmt", "decimal", 8));
            xmlLines.push(createXmlTag("betsPlacedCnt", "integer", 8));
            xmlLines.push(createXmlTag("betsRefundedCnt", "integer", 8));
            xmlLines.push(createXmlTag("betsWonCnt", "integer", 8));
            xmlLines.push(createXmlTag("betsAdjustedCnt", "integer", 8));
            xmlLines.push(createXmlTag("revenueAmt", "decimal", 8));
            xmlLines.push(createXmlTag("networkAdjustAmt", "decimal", 8));
            xmlLines.push(createXmlTag("taxPlayerIncomeAmt", "decimal", 8));
            xmlLines.push(createXmlTag("slipsExpiredCnt", "integer", 8));
            xmlLines.push(createXmlTag("slipsExpiredAmt", "decimal", 8));
            xmlLines.push(
              createXmlTag("fundsType", "string | has_specifics", 8),
            );
            xmlLines.push("      </pariMutuelSummary>");
          }

          // Add pokerCashSummary section if RRI_pokerCash is selected
          if (
            selectedCategories.some((cat) => cat.categoryId === "RRI_pokerCash")
          ) {
            xmlLines.push("      <pokerCashSummary>");
            xmlLines.push(createXmlTag("playerCnt", "integer", 8));
            xmlLines.push(createXmlTag("freePlayerCnt", "integer", 8));
            xmlLines.push(createXmlTag("stakeAmt", "decimal", 8));
            xmlLines.push(createXmlTag("freeStakeAmt", "decimal", 8));
            xmlLines.push(createXmlTag("refundAmt", "decimal", 8));
            xmlLines.push(createXmlTag("freeRefundAmt", "decimal", 8));
            xmlLines.push(createXmlTag("wagerAmt", "decimal", 8));
            xmlLines.push(createXmlTag("baseWinAmt", "decimal", 8));
            xmlLines.push(createXmlTag("jackpotContribAmt", "decimal", 8));
            xmlLines.push(createXmlTag("jackpotWinAmt", "decimal", 8));
            xmlLines.push(createXmlTag("rakeAmt", "decimal", 8));
            xmlLines.push(createXmlTag("gamesPlayedCnt", "integer", 8));
            xmlLines.push(createXmlTag("cashOutAmt", "decimal", 8));
            xmlLines.push(createXmlTag("revenueAmt", "decimal", 8));
            xmlLines.push(createXmlTag("networkAdjustAmt", "decimal", 8));
            xmlLines.push(createXmlTag("taxPlayerIncomeAmt", "decimal", 8));
            xmlLines.push(
              createXmlTag("fundsType", "string | has_specifics", 8),
            );
            xmlLines.push("      </pokerCashSummary>");
          }

          // Add pokerTournSummary section if RRI_pokerTourn is selected
          if (
            selectedCategories.some(
              (cat) => cat.categoryId === "RRI_pokerTourn",
            )
          ) {
            xmlLines.push("      <pokerTournSummary>");
            xmlLines.push(createXmlTag("playerCnt", "integer", 8));
            xmlLines.push(createXmlTag("freePlayerCnt", "integer", 8));
            xmlLines.push(createXmlTag("stakeAmt", "decimal", 8));
            xmlLines.push(createXmlTag("freeStakeAmt", "decimal", 8));
            xmlLines.push(createXmlTag("refundAmt", "decimal", 8));
            xmlLines.push(createXmlTag("freeRefundAmt", "decimal", 8));
            xmlLines.push(createXmlTag("rakeAmt", "decimal", 8));
            xmlLines.push(createXmlTag("baseWinAmt", "decimal", 8));
            xmlLines.push(createXmlTag("jackpotContribAmt", "decimal", 8));
            xmlLines.push(createXmlTag("jackpotWinAmt", "decimal", 8));
            xmlLines.push(createXmlTag("fromPotAmt", "decimal", 8));
            xmlLines.push(createXmlTag("potContribAmt", "decimal", 8));
            xmlLines.push(createXmlTag("potAdditionalAmt", "decimal", 8));
            xmlLines.push(createXmlTag("toPotAmt", "decimal", 8));
            xmlLines.push(createXmlTag("revenueAmt", "decimal", 8));
            xmlLines.push(createXmlTag("networkAdjustAmt", "decimal", 8));
            xmlLines.push(createXmlTag("taxPlayerIncomeAmt", "decimal", 8));
            xmlLines.push(
              createXmlTag("fundsType", "string | has_specifics", 8),
            );
            xmlLines.push("      </pokerTournSummary>");
          }

          // Add fantasySummary section if RRI_fantasy is selected
          if (
            selectedCategories.some((cat) => cat.categoryId === "RRI_fantasy")
          ) {
            xmlLines.push("      <fantasySummary>");
            xmlLines.push(createXmlTag("playerCnt", "integer", 8));
            xmlLines.push(createXmlTag("freePlayerCnt", "integer", 8));
            xmlLines.push(createXmlTag("stakeAmt", "decimal", 8));
            xmlLines.push(createXmlTag("freeStakeAmt", "decimal", 8));
            xmlLines.push(createXmlTag("refundAmt", "decimal", 8));
            xmlLines.push(createXmlTag("freeRefundAmt", "decimal", 8));
            xmlLines.push(createXmlTag("rakeAmt", "decimal", 8));
            xmlLines.push(createXmlTag("baseWinAmt", "decimal", 8));
            xmlLines.push(createXmlTag("fromPotAmt", "decimal", 8));
            xmlLines.push(createXmlTag("potContribAmt", "decimal", 8));
            xmlLines.push(createXmlTag("potAdditionalAmt", "decimal", 8));
            xmlLines.push(createXmlTag("toPotAmt", "decimal", 8));
            xmlLines.push(createXmlTag("revenueAmt", "decimal", 8));
            xmlLines.push(createXmlTag("networkAdjustAmt", "decimal", 8));
            xmlLines.push(createXmlTag("taxPlayerIncomeAmt", "decimal", 8));
            xmlLines.push(
              createXmlTag("fundsType", "string | has_specifics", 8),
            );
            xmlLines.push("      </fantasySummary>");
          }

          // Add bingoSummary section if RRI_bingo is selected
          if (
            selectedCategories.some((cat) => cat.categoryId === "RRI_bingo")
          ) {
            xmlLines.push("      <bingoSummary>");
            xmlLines.push(createXmlTag("bingoId", "string", 8));
            xmlLines.push(createXmlTag("roundId", "string", 8));
            xmlLines.push(createXmlTag("playerCnt", "integer", 8));
            xmlLines.push(createXmlTag("freePlayerCnt", "integer", 8));
            xmlLines.push(createXmlTag("stakeAmt", "decimal", 8));
            xmlLines.push(createXmlTag("freeStakeAmt", "decimal", 8));
            xmlLines.push(createXmlTag("refundAmt", "decimal", 8));
            xmlLines.push(createXmlTag("freeRefundAmt", "decimal", 8));
            xmlLines.push(createXmlTag("rakeAmt", "decimal", 8));
            xmlLines.push(createXmlTag("baseWinAmt", "decimal", 8));
            xmlLines.push(createXmlTag("jackpotContribAmt", "decimal", 8));
            xmlLines.push(createXmlTag("jackpotWinAmt", "decimal", 8));
            xmlLines.push(createXmlTag("fromPotAmt", "decimal", 8));
            xmlLines.push(createXmlTag("potContribAmt", "decimal", 8));
            xmlLines.push(createXmlTag("potAdditionalAmt", "decimal", 8));
            xmlLines.push(createXmlTag("toPotAmt", "decimal", 8));
            xmlLines.push(createXmlTag("revenueAmt", "decimal", 8));
            xmlLines.push(createXmlTag("networkAdjustAmt", "decimal", 8));
            xmlLines.push(createXmlTag("taxPlayerIncomeAmt", "decimal", 8));
            xmlLines.push(createXmlTag("issuedCardCnt", "integer", 8));
            xmlLines.push(createXmlTag("returnedCardCnt", "integer", 8));
            xmlLines.push(
              createXmlTag("fundsType", "string | has_specifics", 8),
            );
            xmlLines.push("      </bingoSummary>");
          }

          // Add landFixedOddsSummary section if RRI_landFixedOdds is selected
          if (
            selectedCategories.some(
              (cat) => cat.categoryId === "RRI_landFixedOdds",
            )
          ) {
            xmlLines.push("      <landFixedOddsSummary>");
            xmlLines.push(createXmlTag("stakeAmt", "decimal", 8));
            xmlLines.push(createXmlTag("freeStakeAmt", "decimal", 8));
            xmlLines.push(createXmlTag("refundAmt", "decimal", 8));
            xmlLines.push(createXmlTag("freeRefundAmt", "decimal", 8));
            xmlLines.push(createXmlTag("forfeitAmt", "decimal", 8));
            xmlLines.push(createXmlTag("baseWinAmt", "decimal", 8));
            xmlLines.push(createXmlTag("adjustmentAmt", "decimal", 8));
            xmlLines.push(createXmlTag("betsPlacedCnt", "integer", 8));
            xmlLines.push(createXmlTag("betsRefundedCnt", "integer", 8));
            xmlLines.push(createXmlTag("betsWonCnt", "integer", 8));
            xmlLines.push(createXmlTag("betsAdjustedCnt", "integer", 8));
            xmlLines.push(createXmlTag("revenueAmt", "decimal", 8));
            xmlLines.push(createXmlTag("networkAdjustAmt", "decimal", 8));
            xmlLines.push(createXmlTag("taxPlayerIncomeAmt", "decimal", 8));
            xmlLines.push(createXmlTag("slipsExpiredCnt", "integer", 8));
            xmlLines.push(createXmlTag("slipsExpiredAmt", "decimal", 8));
            xmlLines.push(
              createXmlTag("fundsType", "string | has_specifics", 8),
            );
            xmlLines.push(createXmlTag("terminalId", "string", 8));
            xmlLines.push(createXmlTag("location", "string", 8));
            xmlLines.push("      </landFixedOddsSummary>");
          }

          xmlLines.push("    </gameSummaryDetail>");

          xmlLines.push("    <gameMovementList>");
          xmlLines.push("      <gameMovement>");
          xmlLines.push(
            createXmlTag("transClass", "string | has_specifics", 8),
          );
          xmlLines.push(createXmlTag("transType", "string | has_specifics", 8));
          xmlLines.push(createXmlTag("fundsType", "string | has_specifics", 8));
          xmlLines.push(createXmlTag("transAmt", "decimal", 8));
          xmlLines.push("      </gameMovement>");
          xmlLines.push("    </gameMovementList>");

          xmlLines.push("  </gameSummary>");
          xmlLines.push("</data>");
          return xmlLines.join("\n");
        },

        game_funds_in_play: () => {
          const selectedCategories =
            this.getSelectedCategoriesForReport("game_funds_in_play");

          const xmlLines = ["<data>"];
          xmlLines.push("  <gameFundsInPlay>");
          xmlLines.push(createXmlTag("periodStart", "date", 4));
          xmlLines.push(createXmlTag("statusDateTime", "datetime", 4));
          xmlLines.push(createXmlTag("brandId", "string", 4));

          xmlLines.push("    <game>");
          xmlLines.push(createXmlTag("gameId", "string", 6));
          xmlLines.push(createXmlTag("gameName", "string", 6));
          xmlLines.push(createXmlTag("gameCategory", "string", 6));
          xmlLines.push(createXmlTag("gameType", "string | has_specifics", 6));
          xmlLines.push(createXmlTag("gameVariant", "string | null", 6));
          xmlLines.push("    </game>");

          xmlLines.push(
            "    <!-- gameInPlayDetail will include different metrics based on selected game categories. -->",
          );
          xmlLines.push(
            "    <!-- Only include relevant sections for the selected category in a record. -->",
          );
          xmlLines.push("    <gameInPlayDetail>");

          // Add casinoGameInPlay section if RRI_casinoGame is selected
          if (
            selectedCategories.some(
              (cat) => cat.categoryId === "RRI_casinoGame",
            )
          ) {
            xmlLines.push("      <casinoGameInPlay>");
            xmlLines.push(createXmlTag("stakeAmt", "decimal", 8));
            xmlLines.push(createXmlTag("freeStakeAmt", "decimal", 8));
            xmlLines.push(createXmlTag("refundAmt", "decimal", 8));
            xmlLines.push(createXmlTag("freeRefundAmt", "decimal", 8));
            xmlLines.push(createXmlTag("wagerAmt", "decimal", 8));
            xmlLines.push(createXmlTag("baseWinAmt", "decimal", 8));
            xmlLines.push(createXmlTag("jackpotContribAmt", "decimal", 8));
            xmlLines.push(createXmlTag("jackpotWinAmt", "decimal", 8));
            xmlLines.push(createXmlTag("gamesPlayedCnt", "integer", 8));
            xmlLines.push(
              createXmlTag("fundsType", "string | has_specifics", 8),
            );
            xmlLines.push("      </casinoGameInPlay>");
          }

          // Add casinoTournInPlay section if RRI_casinoTourn is selected
          if (
            selectedCategories.some(
              (cat) => cat.categoryId === "RRI_casinoTourn",
            )
          ) {
            xmlLines.push("      <casinoTournInPlay>");
            xmlLines.push(createXmlTag("playerCnt", "integer", 8));
            xmlLines.push(createXmlTag("freePlayerCnt", "integer", 8));
            xmlLines.push(createXmlTag("stakeAmt", "decimal", 8));
            xmlLines.push(createXmlTag("freeStakeAmt", "decimal", 8));
            xmlLines.push(createXmlTag("refundAmt", "decimal", 8));
            xmlLines.push(createXmlTag("freeRefundAmt", "decimal", 8));
            xmlLines.push(createXmlTag("rakeAmt", "decimal", 8));
            xmlLines.push(createXmlTag("baseWinAmt", "decimal", 8));
            xmlLines.push(createXmlTag("fromPotAmt", "decimal", 8));
            xmlLines.push(createXmlTag("potContribAmt", "decimal", 8));
            xmlLines.push(createXmlTag("potAdditionalAmt", "decimal", 8));
            xmlLines.push(createXmlTag("toPotAmt", "decimal", 8));
            xmlLines.push(
              createXmlTag("fundsType", "string | has_specifics", 8),
            );
            xmlLines.push("      </casinoTournInPlay>");
          }

          // Add fixedOddsInPlay section if RRI_fixedOdds is selected
          if (
            selectedCategories.some((cat) => cat.categoryId === "RRI_fixedOdds")
          ) {
            xmlLines.push("      <fixedOddsInPlay>");
            xmlLines.push(createXmlTag("stakeAmt", "decimal", 8));
            xmlLines.push(createXmlTag("freeStakeAmt", "decimal", 8));
            xmlLines.push(createXmlTag("refundAmt", "decimal", 8));
            xmlLines.push(createXmlTag("freeRefundAmt", "decimal", 8));
            xmlLines.push(createXmlTag("forfeitAmt", "decimal", 8));
            xmlLines.push(createXmlTag("baseWinAmt", "decimal", 8));
            xmlLines.push(createXmlTag("adjustmentAmt", "decimal", 8));
            xmlLines.push(createXmlTag("betsPlacedCnt", "integer", 8));
            xmlLines.push(createXmlTag("betsRefundedCnt", "integer", 8));
            xmlLines.push(createXmlTag("betsWonCnt", "integer", 8));
            xmlLines.push(createXmlTag("betsAdjustedCnt", "integer", 8));
            xmlLines.push(createXmlTag("slipIssuedCnt", "integer", 8));
            xmlLines.push(createXmlTag("slipUnclaimedCnt", "integer", 8));
            xmlLines.push(createXmlTag("slipUnclaimedAmt", "decimal", 8));
            xmlLines.push(
              createXmlTag("fundsType", "string | has_specifics", 8),
            );
            xmlLines.push("      </fixedOddsInPlay>");
          }

          // Add exchangeInPlay section if RRI_betExchange is selected
          if (
            selectedCategories.some(
              (cat) => cat.categoryId === "RRI_betExchange",
            )
          ) {
            xmlLines.push("      <exchangeInPlay>");
            xmlLines.push(createXmlTag("stakeAmt", "decimal", 8));
            xmlLines.push(createXmlTag("freeStakeAmt", "decimal", 8));
            xmlLines.push(createXmlTag("refundAmt", "decimal", 8));
            xmlLines.push(createXmlTag("freeRefundAmt", "decimal", 8));
            xmlLines.push(createXmlTag("baseWinAmt", "decimal", 8));
            xmlLines.push(createXmlTag("rakeAmt", "decimal", 8));
            xmlLines.push(createXmlTag("adjustmentAmt", "decimal", 8));
            xmlLines.push(createXmlTag("betsPlacedCnt", "integer", 8));
            xmlLines.push(createXmlTag("betsRefundedCnt", "integer", 8));
            xmlLines.push(createXmlTag("betsWonCnt", "integer", 8));
            xmlLines.push(createXmlTag("betsAdjustedCnt", "integer", 8));
            xmlLines.push(
              createXmlTag("fundsType", "string | has_specifics", 8),
            );
            xmlLines.push("      </exchangeInPlay>");
          }

          // Add pariMutuelInPlay section if RRI_pariMutuel is selected
          if (
            selectedCategories.some(
              (cat) => cat.categoryId === "RRI_pariMutuel",
            )
          ) {
            xmlLines.push("      <pariMutuelInPlay>");
            xmlLines.push(createXmlTag("stakeAmt", "decimal", 8));
            xmlLines.push(createXmlTag("freeStakeAmt", "decimal", 8));
            xmlLines.push(createXmlTag("refundAmt", "decimal", 8));
            xmlLines.push(createXmlTag("freeRefundAmt", "decimal", 8));
            xmlLines.push(createXmlTag("baseWinAmt", "decimal", 8));
            xmlLines.push(createXmlTag("adjustmentAmt", "decimal", 8));
            xmlLines.push(createXmlTag("betsPlacedCnt", "integer", 8));
            xmlLines.push(createXmlTag("betsRefundedCnt", "integer", 8));
            xmlLines.push(createXmlTag("betsWonCnt", "integer", 8));
            xmlLines.push(createXmlTag("betsAdjustedCnt", "integer", 8));
            xmlLines.push(createXmlTag("slipIssuedCnt", "integer", 8));
            xmlLines.push(createXmlTag("slipUnclaimedCnt", "integer", 8));
            xmlLines.push(createXmlTag("slipUnclaimedAmt", "decimal", 8));
            xmlLines.push(
              createXmlTag("fundsType", "string | has_specifics", 8),
            );
            xmlLines.push("      </pariMutuelInPlay>");
          }

          // Add pokerCashInPlay section if RRI_pokerCash is selected
          if (
            selectedCategories.some((cat) => cat.categoryId === "RRI_pokerCash")
          ) {
            xmlLines.push("      <pokerCashInPlay>");
            xmlLines.push(createXmlTag("playerCnt", "integer", 8));
            xmlLines.push(createXmlTag("freePlayerCnt", "integer", 8));
            xmlLines.push(createXmlTag("stakeAmt", "decimal", 8));
            xmlLines.push(createXmlTag("freeStakeAmt", "decimal", 8));
            xmlLines.push(createXmlTag("refundAmt", "decimal", 8));
            xmlLines.push(createXmlTag("freeRefundAmt", "decimal", 8));
            xmlLines.push(createXmlTag("wagerAmt", "decimal", 8));
            xmlLines.push(createXmlTag("baseWinAmt", "decimal", 8));
            xmlLines.push(createXmlTag("jackpotContribAmt", "decimal", 8));
            xmlLines.push(createXmlTag("jackpotWinAmt", "decimal", 8));
            xmlLines.push(createXmlTag("rakeAmt", "decimal", 8));
            xmlLines.push(createXmlTag("gamesPlayedCnt", "integer", 8));
            xmlLines.push(createXmlTag("cashOutAmt", "decimal", 8));
            xmlLines.push(
              createXmlTag("fundsType", "string | has_specifics", 8),
            );
            xmlLines.push("      </pokerCashInPlay>");
          }

          // Add pokerTournInPlay section if RRI_pokerTourn is selected
          if (
            selectedCategories.some(
              (cat) => cat.categoryId === "RRI_pokerTourn",
            )
          ) {
            xmlLines.push("      <pokerTournInPlay>");
            xmlLines.push(createXmlTag("playerCnt", "integer", 8));
            xmlLines.push(createXmlTag("freePlayerCnt", "integer", 8));
            xmlLines.push(createXmlTag("stakeAmt", "decimal", 8));
            xmlLines.push(createXmlTag("freeStakeAmt", "decimal", 8));
            xmlLines.push(createXmlTag("refundAmt", "decimal", 8));
            xmlLines.push(createXmlTag("freeRefundAmt", "decimal", 8));
            xmlLines.push(createXmlTag("rakeAmt", "decimal", 8));
            xmlLines.push(createXmlTag("baseWinAmt", "decimal", 8));
            xmlLines.push(createXmlTag("jackpotContribAmt", "decimal", 8));
            xmlLines.push(createXmlTag("jackpotWinAmt", "decimal", 8));
            xmlLines.push(createXmlTag("fromPotAmt", "decimal", 8));
            xmlLines.push(createXmlTag("potContribAmt", "decimal", 8));
            xmlLines.push(createXmlTag("potAdditionalAmt", "decimal", 8));
            xmlLines.push(createXmlTag("toPotAmt", "decimal", 8));
            xmlLines.push(
              createXmlTag("fundsType", "string | has_specifics", 8),
            );
            xmlLines.push("      </pokerTournInPlay>");
          }

          // Add fantasyInPlay section if RRI_fantasy is selected
          if (
            selectedCategories.some((cat) => cat.categoryId === "RRI_fantasy")
          ) {
            xmlLines.push("      <fantasyInPlay>");
            xmlLines.push(createXmlTag("playerCnt", "integer", 8));
            xmlLines.push(createXmlTag("freePlayerCnt", "integer", 8));
            xmlLines.push(createXmlTag("stakeAmt", "decimal", 8));
            xmlLines.push(createXmlTag("freeStakeAmt", "decimal", 8));
            xmlLines.push(createXmlTag("refundAmt", "decimal", 8));
            xmlLines.push(createXmlTag("freeRefundAmt", "decimal", 8));
            xmlLines.push(createXmlTag("rakeAmt", "decimal", 8));
            xmlLines.push(createXmlTag("baseWinAmt", "decimal", 8));
            xmlLines.push(createXmlTag("fromPotAmt", "decimal", 8));
            xmlLines.push(createXmlTag("potContribAmt", "decimal", 8));
            xmlLines.push(createXmlTag("potAdditionalAmt", "decimal", 8));
            xmlLines.push(createXmlTag("toPotAmt", "decimal", 8));
            xmlLines.push(
              createXmlTag("fundsType", "string | has_specifics", 8),
            );
            xmlLines.push("      </fantasyInPlay>");
          }

          // Add bingoInPlay section if RRI_bingo is selected
          if (
            selectedCategories.some((cat) => cat.categoryId === "RRI_bingo")
          ) {
            xmlLines.push("      <bingoInPlay>");
            xmlLines.push(createXmlTag("bingoId", "string", 8));
            xmlLines.push(createXmlTag("roundId", "string", 8));
            xmlLines.push(createXmlTag("playerCnt", "integer", 8));
            xmlLines.push(createXmlTag("freePlayerCnt", "integer", 8));
            xmlLines.push(createXmlTag("stakeAmt", "decimal", 8));
            xmlLines.push(createXmlTag("freeStakeAmt", "decimal", 8));
            xmlLines.push(createXmlTag("refundAmt", "decimal", 8));
            xmlLines.push(createXmlTag("freeRefundAmt", "decimal", 8));
            xmlLines.push(createXmlTag("rakeAmt", "decimal", 8));
            xmlLines.push(createXmlTag("baseWinAmt", "decimal", 8));
            xmlLines.push(createXmlTag("jackpotContribAmt", "decimal", 8));
            xmlLines.push(createXmlTag("jackpotWinAmt", "decimal", 8));
            xmlLines.push(createXmlTag("fromPotAmt", "decimal", 8));
            xmlLines.push(createXmlTag("potContribAmt", "decimal", 8));
            xmlLines.push(createXmlTag("potAdditionalAmt", "decimal", 8));
            xmlLines.push(createXmlTag("toPotAmt", "decimal", 8));
            xmlLines.push(
              createXmlTag("fundsType", "string | has_specifics", 8),
            );
            xmlLines.push("      </bingoInPlay>");
          }

          // Add landFixedOddsInPlay section if RRI_landFixedOdds is selected
          if (
            selectedCategories.some(
              (cat) => cat.categoryId === "RRI_landFixedOdds",
            )
          ) {
            xmlLines.push("      <landFixedOddsInPlay>");
            xmlLines.push(createXmlTag("stakeAmt", "decimal", 8));
            xmlLines.push(createXmlTag("freeStakeAmt", "decimal", 8));
            xmlLines.push(createXmlTag("refundAmt", "decimal", 8));
            xmlLines.push(createXmlTag("freeRefundAmt", "decimal", 8));
            xmlLines.push(createXmlTag("forfeitAmt", "decimal", 8));
            xmlLines.push(createXmlTag("baseWinAmt", "decimal", 8));
            xmlLines.push(createXmlTag("adjustmentAmt", "decimal", 8));
            xmlLines.push(createXmlTag("betsPlacedCnt", "integer", 8));
            xmlLines.push(createXmlTag("betsRefundedCnt", "integer", 8));
            xmlLines.push(createXmlTag("betsWonCnt", "integer", 8));
            xmlLines.push(createXmlTag("betsAdjustedCnt", "integer", 8));
            xmlLines.push(createXmlTag("slipIssuedCnt", "integer", 8));
            xmlLines.push(createXmlTag("slipUnclaimedCnt", "integer", 8));
            xmlLines.push(createXmlTag("slipUnclaimedAmt", "decimal", 8));
            xmlLines.push(
              createXmlTag("fundsType", "string | has_specifics", 8),
            );
            xmlLines.push(createXmlTag("terminalId", "string", 8));
            xmlLines.push(createXmlTag("location", "string", 8));
            xmlLines.push("      </landFixedOddsInPlay>");
          }

          xmlLines.push("    </gameInPlayDetail>");

          xmlLines.push("    <gameMovementList>");
          xmlLines.push("      <gameMovement>");
          xmlLines.push(
            createXmlTag("transClass", "string | has_specifics", 8),
          );
          xmlLines.push(createXmlTag("transType", "string | has_specifics", 8));
          xmlLines.push(createXmlTag("fundsType", "string | has_specifics", 8));
          xmlLines.push(createXmlTag("transAmt", "decimal", 8));
          xmlLines.push("      </gameMovement>");
          xmlLines.push("    </gameMovementList>");

          xmlLines.push("  </gameFundsInPlay>");
          xmlLines.push("</data>");
          return xmlLines.join("\n");
        },

        player_summary: () => {
          const xmlLines = ["<data>"];
          xmlLines.push("  <playerSummary>");
          xmlLines.push(createXmlTag("brandId", "string", 4));

          xmlLines.push("    <player>");
          xmlLines.push(createXmlTag("playerId", "string", 6));
          xmlLines.push("    </player>");

          xmlLines.push("    <playerGameSummary>");
          xmlLines.push("      <game>");
          xmlLines.push(createXmlTag("gameId", "string", 8));
          xmlLines.push(createXmlTag("gameName", "string", 8));
          xmlLines.push(createXmlTag("gameCategory", "string", 8));
          xmlLines.push(createXmlTag("gameType", "string | has_specifics", 8));
          xmlLines.push(createXmlTag("gameVariant", "string|null", 8));
          xmlLines.push("      </game>");

          xmlLines.push("      <playerMovementList>");
          xmlLines.push("        <playerMovement>");
          xmlLines.push(createXmlTag("transType", "string", 10));
          xmlLines.push(
            createXmlTag("fundsType", "string | has_specifics", 10),
          );
          xmlLines.push(createXmlTag("transAmt", "decimal", 10));
          xmlLines.push("        </playerMovement>");
          xmlLines.push("      </playerMovementList>");
          xmlLines.push("    </playerGameSummary>");

          xmlLines.push("  </playerSummary>");
          xmlLines.push("</data>");
          return xmlLines.join("\n");
        },

        player_registration: () => {
          const xmlLines = ["<data>"];
          xmlLines.push("  <playerRegistration>");
          xmlLines.push(
            createXmlTag(
              "brandId",
              "string | This value is got from your operator NCEMS portal.",
              4,
            ),
          );
          xmlLines.push(createXmlTag("transDateTime", "datetime", 4));

          xmlLines.push("    <player>");
          xmlLines.push(
            createXmlTag(
              "playerId",
              "string | Got the operator's CMS. Should be unique for each player &amp; consistent across reports.",
              6,
            ),
          );
          xmlLines.push("    </player>");

          xmlLines.push("    <playerInfo>");
          xmlLines.push(createXmlTag("givenNames", "string", 6));
          xmlLines.push(createXmlTag("familyNames", "string", 6));
          xmlLines.push(createXmlTag("nationality", "string", 6));
          xmlLines.push(createXmlTag("birthDate", "date", 6));
          xmlLines.push(
            createXmlTag("genderType", "string | has_specifics", 6),
          );
          xmlLines.push(createXmlTag("fromDateTime", "datetime", 6));
          xmlLines.push("    </playerInfo>");

          xmlLines.push("    <playerStatusInfo>");
          xmlLines.push(
            createXmlTag("playerStatus", "string | has_specifics", 6),
          );
          xmlLines.push(createXmlTag("operatorStatus", "string", 6));
          xmlLines.push(createXmlTag("fromDateTime", "datetime", 6));
          xmlLines.push(createXmlTag("verifiedDateTime", "datetime", 6));
          xmlLines.push("    </playerStatusInfo>");

          xmlLines.push("    <playerPhoneList>");
          xmlLines.push("      <playerPhone>");
          xmlLines.push(createXmlTag("phoneCountry", "string", 8));
          xmlLines.push(createXmlTag("phoneNumber", "string", 8));
          xmlLines.push(createXmlTag("verifiedDateTime", "datetime", 8));
          xmlLines.push(
            createXmlTag("isDeleted", "boolean | has_specifics", 8),
          );
          xmlLines.push(createXmlTag("fromDateTime", "datetime", 8));
          xmlLines.push("      </playerPhone>");
          xmlLines.push("    </playerPhoneList>");

          xmlLines.push(
            "    <!-- This playerIdentificationList is optional. An empty list can be provided -->",
          );
          xmlLines.push("    <playerIdentificationList>");
          xmlLines.push("      <playerIdentification>");
          xmlLines.push(createXmlTag("idType", "string | has_specifics", 8));
          xmlLines.push(createXmlTag("countryCode", "string", 8));
          xmlLines.push(
            createXmlTag("subDivision", "string | has_specifics", 8),
          );
          xmlLines.push(createXmlTag("idNumber", "string", 8));
          xmlLines.push(createXmlTag("issueDate", "date", 8));
          xmlLines.push(createXmlTag("expirationDate", "date", 8));
          xmlLines.push(
            createXmlTag("isDeleted", "boolean | has_specifics", 8),
          );
          xmlLines.push(createXmlTag("fromDateTime", "datetime", 8));
          xmlLines.push("      </playerIdentification>");
          xmlLines.push("    </playerIdentificationList>");

          xmlLines.push(
            "    <!-- This playerExclusionList is optional. An empty list can be provided -->",
          );
          xmlLines.push("    <playerExclusionList>");
          xmlLines.push("      <playerExclusion>");
          xmlLines.push(
            createXmlTag("exclusionSource", "string | has_specifics", 8),
          );
          xmlLines.push(createXmlTag("startDate", "datetime", 8));
          xmlLines.push(createXmlTag("endDate", "datetime", 8));
          xmlLines.push(createXmlTag("fromTime", "string", 8));
          xmlLines.push(createXmlTag("toTime", "string", 8));
          xmlLines.push(
            createXmlTag("autoContinuance", "boolean | has_specifics", 8),
          );
          xmlLines.push(createXmlTag("fromDateTime", "datetime", 8));
          xmlLines.push("      </playerExclusion>");
          xmlLines.push("    </playerExclusionList>");

          xmlLines.push("  </playerRegistration>");
          xmlLines.push("</data>");
          return xmlLines.join("\n");
        },

        player_activity: () => {
          const xmlLines = ["<data>"];
          xmlLines.push("  <playerActivity>");
          xmlLines.push(
            createXmlTag(
              "brandId",
              "string | This value is got from your operator NCEMS portal.",
              4,
            ),
          );

          xmlLines.push("    <player>");
          xmlLines.push(
            createXmlTag(
              "playerId",
              "string | Got the operator's CMS. Should be unique for each player &amp; consistent across reports.",
              6,
            ),
          );
          xmlLines.push("    </player>");

          xmlLines.push("    <playerActivityDetail>");
          xmlLines.push(createXmlTag("playerActivity", "string", 6));
          xmlLines.push(
            createXmlTag(
              "playerActivityDesc",
              "string|null: ppc:&lt;PaymentServiceProviderCode&gt;;tid:&lt;ExternalTransactionIdentifier&gt;",
              6,
            ),
          );
          xmlLines.push(createXmlTag("playerActivityDateTime", "datetime", 6));
          xmlLines.push(
            "      <!-- transferee is optional. Can be excluded -->",
          );
          xmlLines.push("      <transferee>");
          xmlLines.push(createXmlTag("operatorId", "string", 8));
          xmlLines.push(createXmlTag("externalTransId", "string", 8));
          xmlLines.push(
            createXmlTag("operatorType", "string | has_specifics", 8),
          );
          xmlLines.push("      </transferee>");
          xmlLines.push("    </playerActivityDetail>");

          xmlLines.push(
            "    <!-- startingBalanceList is optional. An empty list can be provided -->",
          );
          xmlLines.push("    <startingBalanceList>");
          xmlLines.push("      <summaryBalance>");
          xmlLines.push(createXmlTag("fundsType", "string", 8));
          xmlLines.push(createXmlTag("startingBalanceAmt", "decimal", 8));
          xmlLines.push("      </summaryBalance>");
          xmlLines.push("    </startingBalanceList>");

          xmlLines.push(
            "    <!-- playerMovementList is optional. An empty list can be provided -->",
          );
          xmlLines.push("    <playerMovementList>");
          xmlLines.push("      <playerMovement>");
          xmlLines.push(createXmlTag("transType", "string | has_specifics", 8));
          xmlLines.push(createXmlTag("fundsType", "string | has_specifics", 8));
          xmlLines.push(createXmlTag("transAmt", "decimal", 8));
          xmlLines.push("      </playerMovement>");
          xmlLines.push("    </playerMovementList>");

          xmlLines.push(
            "    <!-- endingBalanceList is optional. An empty list can be provided -->",
          );
          xmlLines.push("    <endingBalanceList>");
          xmlLines.push("      <summaryBalance>");
          xmlLines.push(createXmlTag("fundsType", "string | has_specifics", 8));
          xmlLines.push(createXmlTag("endingBalanceAmt", "decimal", 8));
          xmlLines.push("      </summaryBalance>");
          xmlLines.push("    </endingBalanceList>");

          xmlLines.push("  </playerActivity>");
          xmlLines.push("</data>");
          return xmlLines.join("\n");
        },

        land_event: () => {
          const xmlLines = ["<data>"];
          xmlLines.push("  <landEvent>");
          xmlLines.push(createXmlTag("brandId", "string", 4));

          xmlLines.push("    <terminal>");
          xmlLines.push(
            createXmlTag(
              "terminalId",
              "string | Got from operator NCEMS portal",
              6,
            ),
          );
          xmlLines.push("    </terminal>");

          xmlLines.push("    <event>");
          xmlLines.push(createXmlTag("eventId", "string", 6));
          xmlLines.push(createXmlTag("eventCode", "string | has_specifics", 6));
          xmlLines.push(createXmlTag("eventText", "string | null", 6));
          xmlLines.push(createXmlTag("eventDateTime", "datetime", 6));
          xmlLines.push("    </event>");

          xmlLines.push("    <associatedData>");
          xmlLines.push(
            "      <!-- voucherData is required for RRI_voucherIssued &amp; RRI_voucherRedeemed eventCodes. -->",
          );
          xmlLines.push("      <voucherData>");
          xmlLines.push(createXmlTag("transPeriod", "string", 8));
          xmlLines.push(createXmlTag("transactionId", "string", 8));
          xmlLines.push(createXmlTag("validationId", "string", 8));
          xmlLines.push(createXmlTag("voucherAmt", "decimal", 8));
          xmlLines.push(createXmlTag("fundsType", "string", 8));
          xmlLines.push("      </voucherData>");

          xmlLines.push(
            "      <!-- gameOutcomeData is required for RRI_gameOutcome &amp; RRI_progressiveAward eventCodes. -->",
          );
          xmlLines.push("      <gameOutcomeData>");
          xmlLines.push(createXmlTag("transPeriod", "string", 8));
          xmlLines.push(createXmlTag("transactionId", "string", 8));
          xmlLines.push(createXmlTag("bonusWinAmt", "decimal", 8));
          xmlLines.push(createXmlTag("jackpotWinAmt", "decimal", 8));
          xmlLines.push(createXmlTag("largeWin", "boolean", 8));
          xmlLines.push(createXmlTag("controllerId", "string", 8));
          xmlLines.push(createXmlTag("levelId", "string", 8));
          xmlLines.push("      </gameOutcomeData>");

          xmlLines.push(
            "      <!-- handpayDisbursementData is required for RRI_handpayDisbursement eventCode. -->",
          );
          xmlLines.push("      <handpayDisbursementData>");
          xmlLines.push(createXmlTag("transPeriod", "string", 8));
          xmlLines.push(createXmlTag("transactionId", "string", 8));
          xmlLines.push(createXmlTag("cashAmt", "decimal", 8));
          xmlLines.push(createXmlTag("handpayType", "string", 8));
          xmlLines.push("      </handpayDisbursementData>");

          xmlLines.push(
            "      <!-- noteAcceptedData is required for RRI_noteAccepted eventCode. -->",
          );
          xmlLines.push("      <noteAcceptedData>");
          xmlLines.push(createXmlTag("transPeriod", "string", 8));
          xmlLines.push(createXmlTag("transactionId", "string", 8));
          xmlLines.push(createXmlTag("noteAmt", "decimal", 8));
          xmlLines.push("      </noteAcceptedData>");

          xmlLines.push(
            "      <!-- watData is required for RRI_watCompleted eventCode. -->",
          );
          xmlLines.push("      <watData>");
          xmlLines.push(createXmlTag("transactionId", "string", 8));
          xmlLines.push(createXmlTag("cardIdentifier", "string", 8));
          xmlLines.push(createXmlTag("transferAmt", "decimal", 8));
          xmlLines.push(createXmlTag("fundsType", "string", 8));
          xmlLines.push(createXmlTag("watType", "string", 8));
          xmlLines.push("      </watData>");
          xmlLines.push("    </associatedData>");

          xmlLines.push("  </landEvent>");
          xmlLines.push("</data>");
          return xmlLines.join("\n");
        },

        jackpot_balance: () => {
          const xmlLines = ["<data>"];
          xmlLines.push("  <jackpotBalance>");
          xmlLines.push(createXmlTag("statusDateTime", "datetime", 4));
          xmlLines.push(createXmlTag("brandId", "string", 4));

          xmlLines.push("    <controller>");
          xmlLines.push(createXmlTag("serverId", "string", 6));
          xmlLines.push(createXmlTag("controllerId", "string", 6));
          xmlLines.push("    </controller>");

          xmlLines.push("    <jackpotOpeningList>");
          xmlLines.push("      <levelBalance>");
          xmlLines.push(createXmlTag("serverId", "string", 8));
          xmlLines.push(createXmlTag("controllerId", "string", 8));
          xmlLines.push(createXmlTag("levelId", "string", 8));
          xmlLines.push(createXmlTag("levelName", "string", 8));
          xmlLines.push(createXmlTag("levelInstance", "string", 8));
          xmlLines.push(createXmlTag("balanceAmt", "decimal", 8));
          xmlLines.push(createXmlTag("overflowAmt", "decimal", 8));
          xmlLines.push(createXmlTag("resetAmt", "decimal", 8));
          xmlLines.push(createXmlTag("ltdWagerAmt", "decimal", 8));
          xmlLines.push(createXmlTag("ltdContribAmt", "decimal", 8));
          xmlLines.push(createXmlTag("ltdOffsetAmt", "decimal", 8));
          xmlLines.push(createXmlTag("ltdResetAmt", "decimal", 8));
          xmlLines.push(createXmlTag("ltdAdjustAmt", "decimal", 8));
          xmlLines.push(createXmlTag("ltdAwardAmt", "decimal", 8));
          xmlLines.push("      </levelBalance>");
          xmlLines.push("    </jackpotOpeningList>");

          xmlLines.push("    <jackpotGameList>");
          xmlLines.push("      <jackpotGame>");
          xmlLines.push("        <game>");
          xmlLines.push(createXmlTag("gameId", "string", 10));
          xmlLines.push(createXmlTag("gameName", "string", 10));
          xmlLines.push(createXmlTag("gameCategory", "string", 10));
          xmlLines.push(createXmlTag("gameType", "string | has_specifics", 10));
          xmlLines.push(createXmlTag("gameVariant", "string|null", 10));
          xmlLines.push("        </game>");
          xmlLines.push("        <jackpotMovementList>");
          xmlLines.push(createXmlTag("transType", "string", 10));
          xmlLines.push(createXmlTag("fundsType", "string", 10));
          xmlLines.push(createXmlTag("transAmt", "decimal", 10));
          xmlLines.push("        </jackpotMovementList>");
          xmlLines.push("      </jackpotGame>");
          xmlLines.push("    </jackpotGameList>");

          xmlLines.push("    <jackpotActivityList>");
          xmlLines.push(createXmlTag("transType", "string | has_specifics", 6));
          xmlLines.push(createXmlTag("fundsType", "string | has_specifics", 6));
          xmlLines.push(createXmlTag("transAmt", "decimal", 6));
          xmlLines.push("    </jackpotActivityList>");

          xmlLines.push("    <jackpotClosingList>");
          xmlLines.push("      <levelBalance>");
          xmlLines.push(createXmlTag("serverId", "string", 8));
          xmlLines.push(createXmlTag("controllerId", "string", 8));
          xmlLines.push(createXmlTag("levelId", "string", 8));
          xmlLines.push(createXmlTag("levelName", "string", 8));
          xmlLines.push(createXmlTag("levelInstance", "string", 8));
          xmlLines.push(createXmlTag("balanceAmt", "decimal", 8));
          xmlLines.push(createXmlTag("overflowAmt", "decimal", 8));
          xmlLines.push(createXmlTag("resetAmt", "decimal", 8));
          xmlLines.push(createXmlTag("ltdWagerAmt", "decimal", 8));
          xmlLines.push(createXmlTag("ltdContribAmt", "decimal", 8));
          xmlLines.push(createXmlTag("ltdOffsetAmt", "decimal", 8));
          xmlLines.push(createXmlTag("ltdResetAmt", "decimal", 8));
          xmlLines.push(createXmlTag("ltdAdjustAmt", "decimal", 8));
          xmlLines.push(createXmlTag("ltdAwardAmt", "decimal", 8));
          xmlLines.push("      </levelBalance>");
          xmlLines.push("    </jackpotClosingList>");

          xmlLines.push("  </jackpotBalance>");
          xmlLines.push("</data>");
          return xmlLines.join("\n");
        },
      };

      if (xmlStructureGenerators[reportId]) {
        return xmlStructureGenerators[reportId]();
      }

      return getDefaultXmlStructure();
    },

    getSelectedCategoriesForReport(reportId) {
      return this.selectedCategories.filter((cat) => cat.reportId === reportId);
    },

    toggleOperationType(type) {
      const available = type === "online" ? this.availableGroups.online.available : this.availableGroups.landBased.available;
      if (!available) return;

      this.selectedOperationTypes[type] = !this.selectedOperationTypes[type];

      const allowedLicenseIds = new Set(this.licensesForSelectedGroups.map((l) => Number(l.license_record_id)));
      this.selectedLicenseRecordIds = this.selectedLicenseRecordIds.filter((id) => allowedLicenseIds.has(Number(id)));

      const allowedReportIds = new Set(this.filteredReports.map((r) => r.id));
      this.selectedReports = this.selectedReports.filter((reportId) => allowedReportIds.has(reportId));
      this.updateCategoriesAfterReportChange();
    },

    toggleReport(reportId) {
      const index = this.selectedReports.indexOf(reportId);
      if (index > -1) {
        this.selectedReports.splice(index, 1);
      } else {
        this.selectedReports.push(reportId);
      }
      this.updateCategoriesAfterReportChange();
    },

    updateCategoriesAfterReportChange() {
      this.selectedCategories = this.selectedCategories.filter((cat) =>
        this.selectedReports.includes(cat.reportId),
      );
    },

    toggleCategory(reportId, categoryId) {
      const index = this.selectedCategories.findIndex(
        (cat) => cat.reportId === reportId && cat.categoryId === categoryId,
      );

      if (index > -1) {
        this.selectedCategories.splice(index, 1);
      } else {
        this.selectedCategories.push({ reportId, categoryId });
      }
    },

    isCategorySelected(reportId, categoryId) {
      return this.selectedCategories.some(
        (cat) => cat.reportId === reportId && cat.categoryId === categoryId,
      );
    },

    getOperationTypeBadgeClass(type) {
      return type === "Online" ? "bg-primary" : "bg-warning";
    },

    getGameCategoriesForReport(reportId) {
      const allCategories = this.getAllCategories();
      const allowedFromLicenses = new Set();
      this.getReportMetaForSelectedLicenses(reportId).forEach((meta) => {
        (meta.allowed_game_unique_names || []).forEach((name) => allowedFromLicenses.add(name));
      });

      let categoryIds = Array.from(allowedFromLicenses);
      if (!categoryIds.length) {
        categoryIds = this.getReportGameCategoryMapping()[reportId] || [];
      }

      return allCategories.filter((category) => categoryIds.includes(category.id));
    },
    getGameCategoriesForReportForLicense(reportId, licenseRecordId) {
      const allCategories = this.getAllCategories();
      const meta = this.getReportMetaForLicense(reportId, licenseRecordId);
      const allowed = meta?.allowed_game_unique_names || [];
      let categoryIds = [...new Set(allowed)];
      if (!categoryIds.length) {
        categoryIds = this.getReportGameCategoryMapping()[reportId] || [];
      }
      return allCategories.filter((category) => categoryIds.includes(category.id));
    },
    getSingleCategoryNameForLicense(reportId, licenseRecordId) {
      const mapping = this.getGameCategoriesForReportForLicense(
        reportId,
        licenseRecordId,
      ).map((cat) => cat.id);
      if (mapping && mapping.length === 1) {
        const category = this.getAllCategories().find((cat) => cat.id === mapping[0]);
        return category ? category.name : mapping[0];
      }
      return "";
    },

    getReportName(reportId) {
      const report = this.getReportById(reportId);
      return report ? report.name : reportId;
    },

    getCategoryName(categoryId) {
      const category = this.getAllCategories().find((c) => c.id === categoryId);
      return category ? category.name : categoryId;
    },

    selectReportForPreview(reportId) {
      this.selectedPreviewReport = reportId;
    },
    isPreviewReportActive(reportId) {
      const current =
        this.selectedPreviewReport ||
        (this.selectedReports.length > 0 ? this.selectedReports[0] : null);
      return current === reportId;
    },

    resetSelections() {
      this.selectedOperationTypes = { online: false, landBased: false };
      this.selectedLicenseRecordIds = [];
      this.selectedReports = [];
      this.selectedCategories = [];
      this.selectedPreviewReport = null;
      this.selectedDataFormat = "1";
      this.activeLicenseRecordId = null;
      this.currentStep = 1;
    },

    async copyToClipboard() {
      const text =
        this.previewFormat === "json" ? this.jsonPreview : this.xmlPreview;

      try {
        await navigator.clipboard.writeText(text);
        this.showNotification("Copied to clipboard!", "success");
      } catch (err) {
        console.error("Failed to copy:", err);
        this.showNotification("Failed to copy to clipboard", "error");
      }
    },

    toggleFullscreen() {
      this.isFullscreen = !this.isFullscreen;
      if (this.isFullscreen) {
        document.body.style.overflow = "hidden";
      } else {
        document.body.style.overflow = "";
      }
    },

    stripJsonComments(jsonString) {
      // Remove single-line (//) and multi-line (/* */) comments from a JSON string
      // while preserving // or /* inside quoted strings
      let result = "";
      let inString = false;
      let stringChar = "";
      let i = 0;

      while (i < jsonString.length) {
        const char = jsonString[i];
        const nextChar = jsonString[i + 1];

        if (inString) {
          result += char;
          if (char === "\\") {
            i++;
            if (i < jsonString.length) result += jsonString[i];
          } else if (char === stringChar) {
            inString = false;
          }
        } else {
          if (char === '"' || char === "'") {
            inString = true;
            stringChar = char;
            result += char;
          } else if (char === "/" && nextChar === "/") {
            // Single-line comment: skip until end of line
            i++;
            while (i < jsonString.length && jsonString[i] !== "\n") {
              i++;
            }
            continue;
          } else if (char === "/" && nextChar === "*") {
            // Multi-line comment: skip until */
            i += 2;
            while (
              i < jsonString.length - 1 &&
              !(jsonString[i] === "*" && jsonString[i + 1] === "/")
            ) {
              i++;
            }
            i += 2;
            continue;
          } else {
            result += char;
          }
        }
        i++;
      }
      return result;
    },

    async handleFinalSubmit() {
      this.isLoading = true;

      try {
        if (!this.selectedLicenses.length) {
          this.showNotification("Please select at least one license.", "error");
          return;
        }

        let savedCount = 0;

        for (const licenseItem of this.selectedLicenses) {
          const reportsForLicense = [];

          this.selectedReports.forEach((reportId) => {
            const reportMeta = (licenseItem.reports || []).find(
              (report) => report.report_id === reportId,
            );
            if (!reportMeta) return;
            if (!reportMeta.is_configurable) return;

            const allowed = reportMeta.allowed_game_unique_names || [];
            if (allowed.length) {
              const picked = this.getSelectedCategoriesForReport(reportId)
                .map((item) => item.categoryId)
                .filter((categoryId) => allowed.includes(categoryId));

              if (picked.length) {
                reportsForLicense.push({
                  report_id: reportId,
                  game_unique_names: picked,
                });
              }
            } else {
              reportsForLicense.push({ report_id: reportId });
            }
          });

          if (!reportsForLicense.length) continue;

          await axios.post("/report-config/selections/save/", {
            license_record_id: licenseItem.license_record_id,
            reports: reportsForLicense,
          });
          savedCount += 1;
        }

        if (!savedCount) {
          this.showNotification(
            "Selected reports are auto-saved for your licenses. No manual configuration was required.",
            "success",
          );
          setTimeout(() => {
            this.$router.push({ name: "View Data Mappings" });
          }, 1500);
          return;
        }

        this.showNotification(
          "All report configurations saved successfully!",
          "success",
        );

        setTimeout(() => {
          this.$router.push({ name: "View Data Mappings" });
        }, 2000);
      } catch (error) {
        console.error("Configuration save error.", error);
        const errorMessage =
          error?.response?.data?.error ||
          "Failed to save configuration. Please try again.";
        this.showNotification(errorMessage, "error");
      } finally {
        this.isLoading = false;
      }
    },
    showNotification(message, type = "success") {
      // Remove any existing notifications
      const existingAlerts = document.querySelectorAll(".fixed-notification");
      existingAlerts.forEach((alert) => alert.remove());

      const notification = document.createElement("div");

      // Base classes
      notification.className =
        "alert alert-dismissible fade show fixed-notification";

      // Set styles based on type
      if (type === "success") {
        notification.style.background =
          "linear-gradient(310deg, #4caf50 0%, #8bc34a 100%)";
        notification.style.color = "white";
        notification.style.border = "none";
        notification.style.boxShadow =
          "0 4px 20px 0 rgba(76, 175, 80, 0.14), 0 7px 10px -5px rgba(76, 175, 80, 0.4)";
      } else if (type === "error") {
        notification.style.background =
          "linear-gradient(310deg, #f44336 0%, #f88a82 100%)";
        notification.style.color = "white";
        notification.style.border = "none";
        notification.style.boxShadow =
          "0 4px 20px 0 rgba(244, 67, 54, 0.14), 0 7px 10px -5px rgba(244, 67, 54, 0.4)";
      }

      // Position styles
      notification.style.position = "fixed";
      notification.style.top = "20px";
      notification.style.right = "20px";
      notification.style.zIndex = "9999";
      notification.style.minWidth = "300px";

      // Create content
      const strong = document.createElement("strong");
      strong.textContent = type === "error" ? "Error! " : "Success! ";
      notification.appendChild(strong);

      const textNode = document.createTextNode(message);
      notification.appendChild(textNode);

      const closeButton = document.createElement("button");
      closeButton.type = "button";
      closeButton.className = "btn-close";
      closeButton.style.filter = "brightness(0) invert(1)";
      closeButton.setAttribute("data-bs-dismiss", "alert");
      closeButton.setAttribute("aria-label", "Close");

      notification.appendChild(closeButton);
      document.body.appendChild(notification);

      setTimeout(() => {
        if (notification.parentNode) {
          notification.remove();
        }
      }, 3000);
    },
  },
};
</script>

<style scoped>
.report-configuration-page {
  min-height: 100vh;
}

.bg-gradient-primary {
  background: linear-gradient(310deg, #f44335 0%, #ef5350 100%);
}

.shadow-primary {
  box-shadow:
    0 4px 20px 0 rgba(0, 0, 0, 0.14),
    0 7px 10px -5px rgba(76, 175, 80, 0.4);
}

.border-radius-lg {
  border-radius: 0.5rem;
}

.z-index-2 {
  z-index: 2;
}

.configuration-card {
  background: #ffffff;
  border-radius: 0.5rem;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
  border: 1px solid #e9ecef;
  padding: 2rem;
  max-width: 1200px;
  margin: 0 auto;
}

.report-section-card {
  border: 1px solid #e9ecef;
  border-radius: 0.5rem;
  overflow: hidden;
}

.report-section-header {
  background: #f8f9fa;
  padding-left: 1rem;
  padding-right: 1rem;
  border-bottom: 1px solid #e9ecef;
}

.report-section-body {
  padding-left: 1rem;
  padding-right: 1rem;
}

.section-card {
  border: 1px solid #e9ecef;
  border-radius: 0.5rem;
  overflow: hidden;
}

.section-header {
  background: #f8f9fa;
  padding-left: 1rem;
  padding-right: 1rem;
  border-bottom: 1px solid #e9ecef;
}

.section-body {
  padding-left: 1rem;
  padding-right: 1rem;
}

.step-indicator {
  max-width: 800px;
  margin: 0 auto 2rem auto;
  padding-bottom: 1rem;
  border-bottom: 1px solid #e9ecef;
}

.step {
  text-align: center;
  flex: 1;
  position: relative;
}

.step-circle {
  width: 30px;
  height: 30px;
  border-radius: 50%;
  background: #dee2e6;
  color: #6c757d;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 6px;
  font-weight: bold;
  transition: all 0.3s ease;
  font-size: 0.75rem;
}

.step.active .step-circle {
  background: #4caf50;
  color: white;
  box-shadow: 0 4px 8px rgba(76, 175, 80, 0.25);
}

.step-label {
  font-size: 0.7rem;
  color: #6c757d;
  font-weight: 500;
  white-space: nowrap;
}

.step.active .step-label {
  color: #4caf50;
  font-weight: bold;
}

.step-divider {
  flex: 1;
  height: 2px;
  background: #dee2e6;
  margin: 18px 5px 0;
}

.operation-card {
  padding: 1.25rem 1rem;
  border: 2px solid #e9ecef;
  border-radius: 0.5rem;
  cursor: pointer;
  transition: all 0.2s ease;
  text-align: center;
  background: #f8f9fa;
  height: 100%;
}

.operation-card:hover {
  border-color: #4caf50;
  background: #f1f8e9;
}

.operation-card.selected {
  border-color: #4caf50;
  background: #e8f5e9;
  box-shadow: 0 0 0 2px rgba(76, 175, 80, 0.1);
}

.operation-icon {
  color: #6c757d;
}

.operation-card.selected .operation-icon {
  color: #4caf50;
}

.operation-card.disabled {
  opacity: 0.45;
  border-color: #dee2e6;
  background: #f8f9fa;
}

.operation-card.disabled:hover {
  border-color: #dee2e6;
  background: #f8f9fa;
}

.report-card {
  padding: 1rem;
  border: 1px solid #e9ecef;
  border-radius: 0.5rem;
  cursor: pointer;
  transition: all 0.2s ease;
  background: #f8f9fa;
  height: 100%;
  position: relative;
  overflow: visible;
}

.report-card:hover {
  border-color: #4caf50;
  background: #f1f8e9;
  transform: translateY(-1px);
}

.report-card.selected {
  border-color: #4caf50;
  background: #e8f5e9;
  box-shadow: 0 0 0 1px rgba(76, 175, 80, 0.1);
}

.report-meta-block {
  display: grid;
  gap: 0.2rem;
}

.requirement-chip {
  position: absolute;
  top: -10px;
  left: 12px;
  padding: 0.2rem 0.55rem;
  border-radius: 999px;
  font-size: 0.68rem;
  font-weight: 700;
  letter-spacing: 0.01em;
  border: 1px solid transparent;
}

.chip-required {
  color: #b42318;
  background: #fef3f2;
  border-color: #fecdca;
}

.chip-optional {
  color: #344054;
  background: #f2f4f7;
  border-color: #d0d5dd;
}

.chip-first-time {
  color: #7a2e0b;
  background: #fff4ed;
  border-color: #fdba74;
}

.chip-default {
  color: #1d2939;
  background: #f8fafc;
  border-color: #e4e7ec;
}

.status-pill {
  display: inline-flex;
  align-items: center;
  border-radius: 999px;
  padding: 0.28rem 0.65rem;
  font-size: 0.74rem;
  font-weight: 700;
}

.status-pill-config {
  color: #9e1c63;
  background: #fce7f3;
}

.status-pill-auto {
  color: #334155;
  background: #e2e8f0;
}

.status-pill-saved {
  color: #166534;
  background: #dcfce7;
}

.category-item {
  padding: 0.75rem;
  border: 1px solid #e9ecef;
  border-radius: 0.375rem;
  cursor: pointer;
  transition: all 0.15s ease;
  background: #f8f9fa;
}

.category-item:hover {
  border-color: #4caf50;
  background: #f1f8e9;
}

.category-item.selected {
  border-color: #4caf50;
  background: #e8f5e9;
}

.license-card.selected {
  border-color: #4caf50;
  background: #e8f5e9;
}

.license-card.license-tab-active {
  border-color: #3b82f6;
  background: #eff6ff;
  box-shadow: 0 0 0 2px rgba(59, 130, 246, 0.2);
}

.preview-tabs {
  display: flex;
  border-bottom: none;
}

.preview-tab {
  padding: 0.25rem 0.75rem;
  background: none;
  border: none;
  color: #6c757d;
  cursor: pointer;
  border-bottom: 2px solid transparent;
  font-size: 0.75rem;
  border-radius: 0.25rem;
  margin-left: 0.5rem;
}

.preview-tab.active {
  color: #4caf50;
  border-bottom-color: #4caf50;
  font-weight: bold;
  background: #f8f9fa;
}

.code-display-box {
  background: #282a36;
  border-radius: 0.5rem;
  padding: 1rem;
  max-height: 250px;
  overflow-y: auto;
  font-family: "Monaco", "Menlo", "Ubuntu Mono", "Consolas", monospace;
  font-size: 1rem;
  line-height: 1.6;
  margin: 0;
  color: #f8f8f2;
  white-space: pre;
  word-break: normal;
  overflow-x: auto;
  border: 1px solid #44475a;
  scrollbar-width: thin;
  scrollbar-color: #5e6a84 #1f2431;
  scrollbar-gutter: stable both-edges;
}

.code-display-box::-webkit-scrollbar {
  width: 9px;
  height: 9px;
}

.code-display-box::-webkit-scrollbar-track {
  background: #1f2431;
  border-radius: 10px;
}

.code-display-box::-webkit-scrollbar-thumb {
  background: linear-gradient(180deg, #6c7a96 0%, #56627a 100%);
  border-radius: 10px;
  border: 2px solid #1f2431;
}

.code-display-box::-webkit-scrollbar-thumb:hover {
  background: linear-gradient(180deg, #7d8da9 0%, #5f6f8d 100%);
}

.code-display-box code {
  background: transparent;
  padding: 0;
  font-size: inherit;
  color: inherit;
  white-space: pre;
}

/* Hide inner highlight.js scrollbar while keeping scroll functionality. */
.code-display-box code.hljs {
  overflow: auto !important;
  scrollbar-width: none;
  -ms-overflow-style: none;
}

.code-display-box code.hljs::-webkit-scrollbar {
  width: 0;
  height: 0;
}

.code-display-box :deep(.field-help-token) {
  position: relative;
  cursor: help;
  text-decoration: underline dotted rgba(160, 198, 255, 0.85);
  text-underline-offset: 2px;
  transition: background-color 0.12s ease;
}

.code-display-box :deep(.field-help-token):hover {
  background: rgba(160, 198, 255, 0.14);
  border-radius: 3px;
}

.code-display-box :deep(.field-help-token)::after {
  content: attr(data-help);
  position: absolute;
  left: calc(100% + 10px);
  top: 50%;
  transform: translateY(-50%);
  width: 300px;
  max-width: 70vw;
  background: #ffffff;
  color: #1f2937;
  border: 1px solid #d1d5db;
  border-radius: 8px;
  padding: 10px;
  font-size: 12px;
  line-height: 1.35;
  white-space: normal;
  opacity: 0;
  pointer-events: none;
  transition: opacity 0.15s ease;
  z-index: 100;
  box-shadow: 0 10px 22px rgba(0, 0, 0, 0.24);
}

.code-display-box :deep(.field-help-token):hover::after {
  opacity: 1;
}

/* Fullscreen Overlay Styles */
.fullscreen-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: #f8f9fa;
  z-index: 9999;
  display: flex;
  flex-direction: column;
}

.fullscreen-overlay .fullscreen-header {
  background: #ffffff;
  padding: 1rem 1.5rem;
  border-bottom: 1px solid #e9ecef;
  display: flex;
  justify-content: space-between;
  align-items: center;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

.fullscreen-overlay .fullscreen-controls {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.fullscreen-overlay .fullscreen-content {
  flex: 1;
  overflow: auto;
  padding: 1.5rem;
}

.code-display-box.fullscreen {
  max-height: none;
  height: 100%;
  border: none;
  border-radius: 0.5rem;
  background: #282a36;
  box-shadow: 0 0 20px rgba(0, 0, 0, 0.3);
}

.clickable-badge {
  cursor: pointer;
  transition: all 0.15s ease;
  padding: 0.25rem 0.5rem;
  font-weight: 500;
}

.clickable-badge:hover {
  transform: translateY(-1px);
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  opacity: 0.9;
}

.clickable-badge-active {
  box-shadow: 0 0 0 2px rgba(59, 130, 246, 0.35);
  outline: 1px solid rgba(59, 130, 246, 0.5);
}

.small {
  font-size: 0.8125rem;
}

@media (max-width: 767.98px) {
  .configuration-card {
    padding: 1.5rem;
    margin: 0 10px;
  }

  .step-label {
    font-size: 0.6rem;
  }

  .step-circle {
    width: 26px;
    height: 26px;
    font-size: 0.7rem;
  }

  .step-divider {
    margin: 16px 2px 0;
  }

  .modal-content {
    width: 95%;
    margin: 1rem;
  }

  .operation-card,
  .report-card {
    padding: 0.75rem;
  }

  .preview-content {
    font-size: 0.9rem;
    padding: 0.75rem;
  }
}

@media (max-width: 575.98px) {
  .configuration-card {
    padding: 1rem;
    margin: 0 5px;
  }

  .step-indicator {
    overflow-x: auto;
  }

  .step {
    min-width: 55px;
  }

  .preview-tab {
    padding: 0.2rem 0.5rem;
    font-size: 0.7rem;
  }
}
</style>
